import {
  allWayStep,
  allWayTogether,
  crosswalkStop,
  dividedBus,
  doNotPass,
  headlights,
  leftTurn,
  roundaboutYield,
  schoolBus,
  stopSign,
  warningSign,
  yieldSign,
} from './dmvDiagrams';
import {
  seedId,
  type ContentCard,
  type ContentLesson,
  type ContentModule,
  type ContentPath,
  type ContentQuiz,
  type ContentTask,
} from '../lib/contentRecords';
import type { LessonSection } from '../lib/roadDiagram';

const pathIds = {
  america: seedId(1),
  budget: seedId(2),
  health: seedId(3),
  renting: seedId(4),
  credit: seedId(5),
  golden: seedId(6),
  self: seedId(7),
  taxes: seedId(8),
};

const moduleIds = {
  welcome: seedId(11),
  arrival: seedId(12),
  budgetSee: seedId(21),
  budgetPlan: seedId(22),
  healthWords: seedId(31),
  healthUse: seedId(32),
};

function bullets(items: string[]): string {
  return items.map((item) => `- ${item}`).join('\n');
}

function task(en: string, my: string): ContentTask {
  return { input_type: 'checkbox', description_en: en, description_my: my, is_optional: false };
}

function display(en: string, my: string): ContentTask {
  return { input_type: 'display', description_en: en, description_my: my, is_optional: false };
}

interface ReadingInput {
  id: number;
  pathId: string;
  moduleId: string;
  slug: string;
  order: number;
  duration: string;
  type?: ContentLesson['lesson_type'];
  titleEn: string;
  titleMy: string;
  skillEn: string;
  skillMy: string;
  introEn: string;
  introMy: string;
  bodyEn: string;
  bodyMy: string;
  takeawaysEn: string[];
  takeawaysMy: string[];
  canEn: string[];
  canMy: string[];
  taskEn: string;
  taskMy: string;
  quiz?: ContentQuiz | null;
  cards?: ContentCard[];
  sections?: LessonSection[];
  practiceTopic?: string | null;
}

function reading(input: ReadingInput): ContentLesson {
  return {
    id: seedId(input.id),
    path_id: input.pathId,
    module_id: input.moduleId,
    slug: input.slug,
    title_en: input.titleEn,
    title_my: input.titleMy,
    skill_en: input.skillEn,
    skill_my: input.skillMy,
    intro_en: input.introEn,
    intro_my: input.introMy,
    body_en: input.bodyEn,
    body_my: input.bodyMy,
    takeaways_en: bullets(input.takeawaysEn),
    takeaways_my: bullets(input.takeawaysMy),
    lesson_type: input.type || 'reading',
    duration: input.duration,
    sort_order: input.order,
    status: 'published',
    is_premium: false,
    tasks: [task(input.taskEn, input.taskMy)],
    deliverables: input.canEn.map((en, index) => display(en, input.canMy[index] || en)),
    quiz: input.quiz || null,
    resources: [],
    cards: input.cards || [],
    sections: input.sections || [],
    practice_topic: input.practiceTopic || null,
  };
}

function moduleShell(
  id: string,
  pathId: string,
  slug: string,
  order: number,
  titleEn: string,
  titleMy: string,
  descriptionEn: string,
  descriptionMy: string,
  lessons: ContentLesson[],
  levelBand: ContentModule['level_band'] = null
): ContentModule {
  return {
    id,
    path_id: pathId,
    slug,
    sort_order: order,
    title_en: titleEn,
    title_my: titleMy,
    description_en: descriptionEn,
    description_my: descriptionMy,
    status: 'published',
    is_premium: false,
    level_band: levelBand,
    lessons,
  };
}

const phoneBodyEn = `## Why a U.S. number matters

Banks, employers, and schools use a phone number to confirm it is really you. Keep your home-country phone for family. Get a U.S. number in your first days.

### Bring this

- Passport
- A second ID if you have one
- The address where you are staying
- A number you can pay every month

### Prepaid or postpaid

A prepaid plan is paid ahead and usually does not need a credit check. A postpaid plan bills you monthly and often asks for a Social Security number and a credit check.

Compare the total price, not only the advertised rate.

- Does it include a U.S. number?
- Does it include hotspot data?
- What do calls home cost?

[FCC consumer guide](https://www.fcc.gov/consumers)

![Phone setup checklist](/images/lesson-phone.svg)

Never share a verification code. A real bank will not ask you to read a text code to a caller.
`;

const phoneBodyMy = `## အမေရိကန်ဖုန်းနံပါတ် ဘာကြောင့် လိုသလဲ

ဘဏ်၊ အလုပ်ရှင်၊ နှင့် ကျောင်းတွေက သင်ကိုယ်တိုင် ဟုတ်မဟုတ် ဖုန်းနံပါတ်နဲ့ အတည်ပြုပါတယ်။ မိသားစုအတွက် မူရင်းနိုင်ငံဖုန်းကို ဆက်သုံးနိုင်ပါတယ်။ ပထမရက်ပိုင်းမှာ အမေရိကန်နံပါတ်တစ်ခု ရယူပါ။

### ကြိုတင်ယူသွားရန်

- ပတ်စ်ပို့
- ရှိရင် ဒုတိယအထောက်အထား
- နေထိုင်နေတဲ့ လိပ်စာ
- တစ်လ ဘယ်လောက်ပေးနိုင်သလဲ

### ကြိုတင်ငွေဖြည့် မဟုတ်ရင် လစဉ်ဘီလ်

ကြိုတင်ငွေဖြည့် (prepaid) အစီအစဉ်က ခရက်ဒစ်စစ်ဆေးမှု မလိုတတ်ပါ။ လစဉ်ဘီလ် (postpaid) က Social Security နံပါတ်နဲ့ ခရက်ဒစ်ကို မေးတတ်ပါတယ်။

ကြော်ငြာဈေးချည်း မကြည့်ပါနဲ့။ စုစုပေါင်းဈေးကို ကြည့်ပါ။

- အမေရိကန်နံပါတ် ပါသလား
- ဟော့စပေါ့ ဒေတာ ပါသလား
- အိမ်ကို ခေါ်ရင် ဘယ်လောက်ကျသလဲ

[FCC စားသုံးသူလမ်းညွှန်](https://www.fcc.gov/consumers)

![ဖုန်းစစ်ဆေးစာရင်း](/images/lesson-phone.svg)

အတည်ပြုကုဒ်ကို ဘယ်သူ့ကိုမှ မပေးပါနဲ့။ ဘဏ်အစစ်က စာတိုထဲက ကုဒ်ကို ဖတ်ခိုင်းလေ့မရှိပါ။
`;

const americaLessons: ContentLesson[] = [
  reading({
    id: 101,
    pathId: pathIds.america,
    moduleId: moduleIds.welcome,
    slug: 'quiz-1',
    order: 1,
    duration: '10 min',
    type: 'quiz',
    titleEn: 'Orientation check',
    titleMy: 'အစပိုင်း စစ်ဆေးချက်',
    skillEn: 'Check what you remember before you move on',
    skillMy: 'ရှေ့ဆက်မီ အခြေခံကို ပြန်စစ်ပါ',
    introEn: 'Pause and check the basics: who to call in an emergency, and one custom that will help you on an ordinary day.',
    introMy: 'ခဏရပ်ပြီး စစ်ပါ။ အရေးပေါ်မှာ ဘယ်သူ့ကို ခေါ်မလဲ၊ နေ့စဉ်ဘဝအတွက် ဓလေ့တစ်ခု ဘာလဲ။',
    bodyEn: `## This is a check, not a test you can fail

If you miss one, go back and read it again. You do not need perfect English. You need a number you can dial and a habit you can use this week.

### Remember

- **911** is for police, fire, and medical emergencies. The call is free.
- "How are you?" is often just a greeting. "Good, thanks" is enough.

Keep the answer practical. Write it down if you want to.
`,
    bodyMy: `## ဒါက ကျရှုံးလို့မရတဲ့ စာမေးပွဲ မဟုတ်ပါ

တစ်ခု လွဲရင် ပြန်ဖတ်ပါ။ အင်္ဂလိပ်စကား ပြည့်စုံဖို့ မလိုပါ။ ခေါ်လို့ရတဲ့ နံပါတ်တစ်ခုနဲ့ ဒီတစ်ပတ် သုံးနိုင်တဲ့ အလေ့အကျင့်တစ်ခု လိုပါတယ်။

### မှတ်ထားပါ

- **911** က ရဲ၊ မီး၊ နှင့် ဆေးဘက် အရေးပေါ်အတွက်ပါ။ ခေါ်ရင် အခမဲ့ပါ။
- "How are you?" က နှုတ်ဆက်တာ ဖြစ်တတ်ပါတယ်။ "Good, thanks" လုံလောက်ပါတယ်။

အဖြေကို လက်တွေ့ကျအောင် ထားပါ။ ချရေးချင်ရင် ရေးထားပါ။
`,
    takeawaysEn: [
      '911 is for emergencies: police, fire, and medical help. It is free to call.',
      'If you are unsure, review the lesson and try the check again.',
    ],
    takeawaysMy: [
      '911 က အရေးပေါ်အတွက်ပါ။ ခေါ်ရင် အခမဲ့ပါ။',
      'မသေချာရင် ပြန်ဖတ်ပြီး နောက်တစ်ခေါက် စစ်ပါ။',
    ],
    canEn: ['Name the emergency number and one reason you would call it.'],
    canMy: ['အရေးပေါ်နံပါတ်ကို ပြောနိုင်ပြီး ဘာကြောင့် ခေါ်မလဲ တစ်ခု ပြောနိုင်သည်။'],
    taskEn: 'Save 911 in your phone and write one sentence about when you would use it.',
    taskMy: 'ဖုန်းထဲမှာ 911 ကို သိမ်းပြီး ဘယ်အချိန် သုံးမလဲ စာကြောင်းတစ်ကြောင်း ရေးပါ။',
    quiz: {
      title_en: 'Orientation check',
      title_my: 'အစပိုင်း စစ်ဆေးချက်',
      slug: 'orientation-check',
      questions: [
        {
          question_en: 'Which number do you call for police, fire, or medical emergencies in the United States?',
          question_my: 'အမေရိကန်မှာ ရဲ၊ မီး၊ သို့မဟုတ် ဆေးဘက်အရေးပေါ်အတွက် ဘယ်နံပါတ်ကို ခေါ်မလဲ။',
          options_en: ['311', '411', '911', '811'],
          options_my: ['311', '411', '911', '811'],
          correct_answer: 2,
          explanation_en: '911 reaches emergency services. 311 is often for non-emergency city services, and it is not available everywhere.',
          explanation_my: '911 က အရေးပေါ်ဝန်ဆောင်မှုကို ရောက်ပါတယ်။ 311 က မြို့၏ အရေးမကြီးသော ဝန်ဆောင်မှုဖြစ်တတ်ပြီး နေရာတိုင်း မရှိပါ။',
        },
        {
          question_en: 'A friend says "How are you?" in passing at the store. What is a normal reply?',
          question_my: 'ဆိုင်မှာ တစ်စုံတစ်ယောက်က "How are you?" လို့ နှုတ်ဆက်ရင် ဘယ်လိုပြန်ရမလဲ။',
          options_en: [
            'Give your full medical history',
            'A short reply such as "Good, thanks" is enough',
            'Ignore them; the question is a trick',
            'Ask to see their ID',
          ],
          options_my: [
            'ကျန်းမာရေးမှတ်တမ်း အားလုံး ပြောပါ',
            '"Good, thanks" လို တိုတိုပြန်ရင် လုံလောက်ပါတယ်',
            'လျစ်လျူရှုပါ။ မေးခွန်းက ထောင်ချောက်ပါ',
            'သူတို့ရဲ့ ID ကို တောင်းပါ',
          ],
          correct_answer: 1,
          explanation_en: 'In everyday American English, "How are you?" is often a greeting, not a request for a long answer.',
          explanation_my: 'နေ့စဉ်အမေရိကန်အင်္ဂလိပ်မှာ "How are you?" က နှုတ်ဆက်တာဖြစ်တတ်ပြီး အဖြေရှည် တောင်းတာ မဟုတ်ပါ။',
        },
      ],
    },
  }),
  reading({
    id: 102,
    pathId: pathIds.america,
    moduleId: moduleIds.arrival,
    slug: 'setting-up-your-phone',
    order: 1,
    duration: '15 min',
    titleEn: 'Setting Up Your Phone',
    titleMy: 'ဖုန်းနံပါတ် စတင်တပ်ဆင်ခြင်း',
    skillEn: 'Get a U.S. phone number you can use for banks, jobs, and verification codes',
    skillMy: 'ဘဏ်၊ အလုပ်၊ နှင့် အတည်ပြုကုဒ်အတွက် အမေရိကန်ဖုန်းနံပါတ် ရယူပါ',
    introEn: 'A U.S. phone number is how banks, employers, and schools confirm it is really you.',
    introMy: 'အမေရိကန်ဖုန်းနံပါတ်က ဘဏ်၊ အလုပ်ရှင်၊ နှင့် ကျောင်းက သင်ကိုယ်တိုင် ဟုတ်မဟုတ် အတည်ပြုတဲ့ နည်းလမ်းပါ။',
    bodyEn: phoneBodyEn,
    bodyMy: phoneBodyMy,
    takeawaysEn: [
      'A prepaid U.S. number is a solid first step when you do not have credit yet.',
      'Never share one-time codes. Real banks do not ask you to read them aloud.',
    ],
    takeawaysMy: [
      'ခရက်ဒစ် မရှိသေးရင် ကြိုတင်ငွေဖြည့် အမေရိကန်နံပါတ်က ပထမခြေလှမ်းကောင်းပါတယ်။',
      'တစ်ခါသုံးကုဒ်ကို မမျှဝေပါနဲ့။ ဘဏ်အစစ်က ဖတ်ခိုင်းလေ့မရှိပါ။',
    ],
    canEn: ['Choose a prepaid or postpaid plan and say why it fits your first month.'],
    canMy: ['ကြိုတင်ငွေဖြည့် သို့မဟုတ် လစဉ်ဘီလ်ကို ရွေးပြီး ပထမလအတွက် ဘာကြောင့် ကိုက်ညီသလဲ ပြောနိုင်သည်။'],
    taskEn: 'Write down two phone plans, their monthly price, and whether they require a credit check.',
    taskMy: 'ဖုန်းအစီအစဉ်နှစ်ခု၊ လစဉ်ဈေး၊ နှင့် ခရက်ဒစ်စစ်ဆေးမှု လိုမလို ရေးချပါ။',
  }),
  reading({
    id: 103,
    pathId: pathIds.america,
    moduleId: moduleIds.arrival,
    slug: 'intenet',
    order: 2,
    duration: '12 min',
    titleEn: 'Internet at Home',
    titleMy: 'အိမ်တွင် အင်တာနက်',
    skillEn: 'Get online without signing a contract you cannot leave',
    skillMy: 'ထွက်လို့မရတဲ့ စာချုပ်မချုပ်ဘဲ အွန်လိုင်းတက်ပါ',
    introEn: 'You need internet for jobs, school, maps, and calls home. You do not have to sign a long contract on day one.',
    introMy: 'အလုပ်၊ ကျောင်း၊ မြေပုံ၊ နှင့် အိမ်ကို ဖုန်းခေါ်ဖို့ အင်တာနက် လိုပါတယ်။ ပထမနေ့မှာ ရှည်တဲ့ စာချုပ်ချုပ်စရာ မလိုပါ။',
    bodyEn: `## Start without a long contract

A phone hotspot can cover the first week. It is slower for several people, but it works while you learn the neighborhood. Public libraries also offer free Wi-Fi and computers.

### Before you order home internet

- Ask the landlord what is already included
- Ask which companies serve the building
- Ask the price after the promotion ends
- Ask the equipment fee and the fee to cancel

A month-to-month plan is safer if you might move.

Use the address on your lease or a letter you can show. Keep the confirmation email. That name and address also help when you open a bank account.
`,
    bodyMy: `## ရှည်တဲ့ စာချုပ် မချုပ်ဘဲ စပါ

ပထမအပတ်ကို ဖုန်းဟော့စပေါ့နဲ့ ကာမိနိုင်ပါတယ်။ လူအများသုံးရင် နှေးပေမယ့် ရပ်ကွက်ကို လေ့လာနေချိန်မှာ အသုံးဝင်ပါတယ်။ အများပြည်သူစာကြည့်တိုက်မှာ အခမဲ့ Wi-Fi နဲ့ ကွန်ပျူတာ ရှိပါတယ်။

### အိမ်သုံးအင်တာနက် မမှာခင်

- အိမ်ရှင်က ဘာအပါအဝင်လဲ မေးပါ
- ဘယ်ကုမ္ပဏီက အဆောက်အအုံကို ဝန်ဆောင်မှုပေးသလဲ မေးပါ
- ပရိုမိုရှင်းပြီးရင် ဈေးဘယ်လောက်လဲ မေးပါ
- စက်ပစ္စည်းကြေးနဲ့ ပယ်ဖျက်ခ မေးပါ

ပြောင်းရွှေ့နိုင်ရင် လစဉ်စာချုပ်က ပိုလုံခြုံပါတယ်။

ငှားရမ်းစာချုပ်ပေါ်က လိပ်စာကို သုံးပါ။ အတည်ပြုအီးမေးလ်ကို သိမ်းပါ။ ဘဏ်အကောင့်ဖွင့်တဲ့အခါ အဲဒီနာမည်နဲ့ လိပ်စာက အထောက်အကူဖြစ်ပါတယ်။
`,
    takeawaysEn: [
      'Library Wi-Fi and a phone hotspot are enough while you compare home plans.',
      'Ask for the price after the promotion and the fee to cancel.',
    ],
    takeawaysMy: [
      'စာကြည့်တိုက် Wi-Fi နဲ့ ဖုန်းဟော့စပေါ့က အစီအစဉ်တွေ နှိုင်းနေချိန်မှာ လုံလောက်ပါတယ်။',
      'ပရိုမိုရှင်းပြီးဈေးနဲ့ ပယ်ဖျက်ခကို မေးပါ။',
    ],
    canEn: ['Name one temporary way to get online and one home plan to compare.'],
    canMy: ['ယာယီအွန်လိုင်းတက်နည်းတစ်ခုနဲ့ နှိုင်းမည့် အိမ်သုံးအစီအစဉ်တစ်ခု ပြောနိုင်သည်။'],
    taskEn: 'Write the address you will use and one internet provider you will call or check online.',
    taskMy: 'သုံးမည့်လိပ်စာနဲ့ ဆက်သွယ်မည့် အင်တာနက်ကုမ္ပဏီတစ်ခုကို ရေးချပါ။',
  }),
];

const budgetLessons: ContentLesson[] = [
  reading({
    id: 201,
    pathId: pathIds.budget,
    moduleId: moduleIds.budgetSee,
    slug: 'first-us-budget',
    order: 1,
    duration: '12 min',
    titleEn: 'What a first U.S. budget includes',
    titleMy: 'ပထမဆုံး အမေရိကန်ဘတ်ဂျက်ထဲမှာ ဘာပါသလဲ',
    skillEn: 'Name the bills that have to be paid before anything else',
    skillMy: 'အခြားအရာတွေမတိုင်ခင် ပေးရမည့် ဘီလ်တွေကို ခေါ်နိုင်သည်',
    introEn: 'A budget is a plan for the money you already have. It is not a test of whether you earn enough.',
    introMy: 'ဘတ်ဂျက်က သင့်ဆီ ရောက်ပြီးသား ငွေအတွက် အစီအစဉ်ပါ။ ဝင်ငွေ လုံလောက်မှု စာမေးပွဲ မဟုတ်ပါ။',
    bodyEn: `## Start with money you can spend

Write the pay that reaches you this month, after tax. Add help from family or savings you brought. Do not write the salary before taxes.

### Needs come first

- Rent
- Food
- Phone
- Transport
- Any debt payment

In many cities rent is the largest bill. A common guide is to keep rent near a third of take-home pay. The real rule is simpler: you must be able to pay it every month.

Put wants on a separate list: eating out, new clothes, extra money sent home. You can still do those things. The budget shows what is left after needs.
`,
    bodyMy: `## သုံးလို့ရတဲ့ ငွေကနေ စပါ

ဒီလ သင့်အကောင့်ထဲ ရောက်တဲ့ လစာကို အခွန်ပြီးမှ ရေးပါ။ မိသားစုအကူအညီ သို့မဟုတ် ယူလာတဲ့ စုငွေကို ထည့်ပါ။ အခွန်မဆောင်ခင် လစာကို မရေးပါနဲ့။

### လိုအပ်ချက်ကို အရင်ထားပါ

- အိမ်ငှားခ
- အစားအစာ
- ဖုန်း
- သွားလာရေး
- အကြွေးပေးချေမှု

မြို့အများစုမှာ အိမ်ငှားခက အကြီးဆုံးဘီလ်ပါ။ လက်ခံလစာရဲ့ သုံးပုံတစ်ပုံဝန်းကျင်ထားဖို့ အကြံပြုတတ်ပါတယ်။ တကယ့်စည်းက ပိုရိုးရှင်းပါတယ်။ လတိုင်း ပေးနိုင်ရပါမယ်။

လိုချင်တာတွေကို သီးသန့်စာရင်းလုပ်ပါ။ အပြင်စား၊ အဝတ်အသစ်၊ အိမ်ကို ပိုပို့တဲ့ငွေ။ အဲဒါတွေ လုပ်လို့ရပါသေးတယ်။ ဘတ်ဂျက်က လိုအပ်ချက်ပြီးရင် ဘာကျန်သလဲ ပြပါတယ်။
`,
    takeawaysEn: ['Budget with the money that reaches your account.', 'Rent, food, phone, and transport come before wants.'],
    takeawaysMy: ['အကောင့်ထဲ ရောက်တဲ့ ငွေနဲ့ ဘတ်ဂျက်ဆွဲပါ။', 'အိမ်ငှားခ၊ အစားအစာ၊ ဖုန်း၊ သွားလာရေးကို လိုချင်တာတွေမတိုင်ခင် ထားပါ။'],
    canEn: ['Separate needs from wants for one month', 'Write down take-home pay, not the pre-tax number'],
    canMy: ['တစ်လစာ လိုအပ်ချက်နဲ့ လိုချင်တာကို ခွဲနိုင်သည်', 'အခွန်မတိုင်ခင် ဂဏန်းမဟုတ်ဘဲ လက်ခံလစာကို ရေးနိုင်သည်'],
    taskEn: 'Write this month’s take-home pay and your four largest needs.',
    taskMy: 'ဒီလ လက်ခံလစာနဲ့ အကြီးဆုံး လိုအပ်ချက်လေးခုကို ရေးပါ။',
  }),
  reading({
    id: 202,
    pathId: pathIds.budget,
    moduleId: moduleIds.budgetSee,
    slug: 'track-one-week',
    order: 2,
    duration: '10 min',
    titleEn: 'Track one week of spending',
    titleMy: 'တစ်ပတ်စာ သုံးစွဲမှု မှတ်ပါ',
    skillEn: 'Notice small costs that add up',
    skillMy: 'စုလာတဲ့ သေးငယ်တဲ့ ကုန်ကျစရိတ်ကို သတိထားပါ',
    introEn: 'You cannot fix a budget you have not seen. One week of notes is enough to start.',
    introMy: 'မမြင်ရသေးတဲ့ ဘတ်ဂျက်ကို ပြင်လို့ မရပါ။ တစ်ပတ်စာ မှတ်စုနဲ့ စလို့ ရပါတယ်။',
    bodyEn: `## Write every purchase for seven days

Include cash, debit cards, and apps. Include the coffee and the bus fare. A notes app or a piece of paper is enough.

### At the end of the week

1. Group the lines into food, transport, phone, and other.
2. Circle anything that surprised you.
3. Do not judge the week.

If you share costs with family, track what you personally pay, then add shared bills once.
`,
    bodyMy: `## ခုနစ်ရက် အသုံးစရိတ်အားလုံး ရေးပါ

ငွေသား၊ ဒက်ဘစ်ကတ်၊ နှင့် အက်ပ်တွေ အပါအဝင်ပါ။ ကော်ဖီနဲ့ ဘတ်စ်ကားခကိုလည်း ထည့်ပါ။ မှတ်စုအက်ပ် သို့မဟုတ် စာရွက်တစ်ရွက် လုံလောက်ပါတယ်။

### တစ်ပတ်အဆုံးမှာ

1. အစားအစာ၊ သွားလာရေး၊ ဖုန်း၊ နှင့် အခြား ဆိုပြီး အုပ်စုဖွဲ့ပါ။
2. အံ့ဩစရာကို ဝိုင်းပါ။
3. အဲဒီတစ်ပတ်ကို အပြစ်မတင်ပါနဲ့။

မိသားစုနဲ့ မျှသုံးရင် ကိုယ်ပေးတဲ့အရာကို အရင်မှတ်ပြီး မျှတဲ့ဘီလ်ကို တစ်ကြိမ်ထည့်ပါ။
`,
    takeawaysEn: ['Track cash and cards together or the picture will be wrong.', 'Look for surprises, not perfection.'],
    takeawaysMy: ['ငွေသားနဲ့ ကတ်ကို အတူမှတ်မှ ပုံမှန်ပါမယ်။', 'ပြည့်စုံမှုမဟုတ်ဘဲ အံ့ဩစရာကို ရှာပါ။'],
    canEn: ['Keep a seven-day spending list', 'Group spending into a few categories'],
    canMy: ['ခုနစ်ရက်စာ သုံးစွဲမှုစာရင်းထားနိုင်သည်', 'အမျိုးအစားအနည်းငယ်ခွဲနိုင်သည်'],
    taskEn: 'Start a seven-day list today and add the first three things you already paid for.',
    taskMy: 'ဒီနေ့ ခုနစ်ရက်စာရင်းစပြီး ပေးပြီးသား သုံးခုကို ထည့်ပါ။',
  }),
  reading({
    id: 203,
    pathId: pathIds.budget,
    moduleId: moduleIds.budgetPlan,
    slug: 'needs-then-wants',
    order: 1,
    duration: '12 min',
    titleEn: 'Pay needs first, then choose wants',
    titleMy: 'လိုအပ်ချက်ကို အရင်ပေး၊ ပြီးမှ လိုချင်တာ ရွေးပါ',
    skillEn: 'Decide what gets paid on payday',
    skillMy: 'လစာထုတ်တဲ့နေ့ ဘာကို အရင်ပေးမလဲ ဆုံးဖြတ်ပါ',
    introEn: 'Payday is the moment to move money to needs before it disappears on small purchases.',
    introMy: 'လစာထုတ်တဲ့နေ့က သေးငယ်တဲ့ ဝယ်ယူမှုတွေမှာ ပျောက်မသွားခင် လိုအပ်ချက်ဆီ ငွေရွှေ့ရမည့် အချိန်ပါ။',
    bodyEn: `## On payday, set needs aside first

Pay or set aside rent, utilities, transport, and a food amount the day you are paid. Many people move rent into a separate savings bucket the same day.

### A weekly food amount

Groceries are usually cheaper than eating out. A simple lunch from home a few days a week changes the month more than skipping one coffee.

Money you send to family is a real bill for many newcomers. Put a repeatable amount in the plan. A smaller regular amount is safer than a large one you cannot sustain.
`,
    bodyMy: `## လစာထုတ်တဲ့နေ့မှာ လိုအပ်ချက်ကို အရင် ခွဲထားပါ

လစာရတဲ့နေ့မှာ အိမ်ငှားခ၊ မီတာခ၊ သွားလာရေး၊ နှင့် အစားအစာပမာဏကို ပေးပါ သို့မဟုတ် ခွဲထားပါ။ လူအများက အိမ်ငှားခကို အဲဒီနေ့မှာ သီးသန့်စုငွေထဲ ရွှေ့ပါတယ်။

### တစ်ပတ်စာ အစားအစာပမာဏ

ကုန်စုံက အပြင်စားတာထက် များသောအားဖြင့် သက်သာပါတယ်။ တစ်ပတ် အိမ်က နေ့လည်စာ အနည်းငယ်က ကော်ဖီတစ်ခွက် ဖြတ်တာထက် လကို ပိုပြောင်းပါတယ်။

မိသားစုဆီ ပို့တဲ့ငွေက လူသစ်အများစုအတွက် တကယ့်ဘီလ်ပါ။ ထပ်လုပ်နိုင်တဲ့ ပမာဏကို အစီအစဉ်ထဲ ထည့်ပါ။ မတည်တံ့နိုင်တဲ့ ပမာဏကြီးထက် ပုံမှန်ပမာဏငယ်က ပိုလုံခြုံပါတယ်။
`,
    takeawaysEn: ['Move rent money aside on payday.', 'Treat support for family as a planned amount, not a leftover.'],
    takeawaysMy: ['လစာထုတ်တဲ့နေ့ အိမ်ငှားခကို ခွဲထားပါ။', 'မိသားစုထောက်ပံ့ငွေကို ကျန်ငွေမဟုတ်ဘဲ စီစဉ်ထားတဲ့ ပမာဏအဖြစ် ထားပါ။'],
    canEn: ['Assign payday money to needs before wants', 'Choose a weekly food amount'],
    canMy: ['လစာငွေကို လိုချင်တာမတိုင်ခင် လိုအပ်ချက်ကို ခွဲနိုင်သည်', 'တစ်ပတ်စာ အစားအစာပမာဏ ရွေးနိုင်သည်'],
    taskEn: 'Write the order you will pay bills on your next payday, starting with housing.',
    taskMy: 'နောက်လစာထုတ်ရင် ဘီလ်ပေးမည့် အစဉ်ကို အိမ်ကနေ စပြီး ရေးပါ။',
  }),
  reading({
    id: 204,
    pathId: pathIds.budget,
    moduleId: moduleIds.budgetPlan,
    slug: 'small-emergency-cushion',
    order: 2,
    duration: '10 min',
    titleEn: 'Start a small emergency cushion',
    titleMy: 'အရေးပေါ်အတွက် သေးငယ်တဲ့ အရန်ငွေ စပါ',
    skillEn: 'Keep a little cash ready for a surprise bill',
    skillMy: 'မထင်မှတ်တဲ့ ဘီလ်အတွက် ငွေအနည်းငယ် အသင့်ထားပါ',
    introEn: 'An emergency cushion is money you do not spend on ordinary days. It can start very small.',
    introMy: 'အရေးပေါ်အရန်ငွေက ပုံမှန်နေ့တွေမှာ မသုံးတဲ့ ငွေပါ။ အရမ်းသေးသေးလေးကနေ စလို့ ရပါတယ်။',
    bodyEn: `## A useful first target is $200 to $500

That can cover a transit card, a clinic copay, or a few days of food if a paycheck is late. You do not need three months of expenses before you begin.

### Each payday

Move a fixed small amount, even $10 or $20, into savings or an envelope you do not touch. Automate it if the bank lets you. Skipping a week is better than quitting.

Use the cushion for surprises. A late bus is not an emergency. A broken phone you need for work can be. When you use it, start filling it again next payday.
`,
    bodyMy: `## ပထမပစ်မှတ်အဖြစ် ဒေါ်လာ ၂၀၀ ကနေ ၅၀၀

အဲဒါက ဘတ်စ်ကတ်၊ ဆေးခန်း copay၊ သို့မဟုတ် လစာနောက်ကျရင် ရက်အနည်းငယ်စာ အစားအစာအတွက် လုံလောက်နိုင်ပါတယ်။ မစခင် သုံးလစာ စုစရာ မလိုပါ။

### လစာတိုင်း

ဒေါ်လာ ၁၀ သို့မဟုတ် ၂၀ လောက်ကို မထိတဲ့ စုငွေ သို့မဟုတ် စာအိတ်ထဲ ရွှေ့ပါ။ ဘဏ်က ခွင့်ပြုရင် အလိုအလျောက်လုပ်ပါ။ ရပ်လိုက်တာထက် တစ်ပတ်ကျော်တာက ပိုကောင်းပါတယ်။

အံ့ဩစရာအတွက်ပဲ သုံးပါ။ ဘတ်စ်နောက်ကျတာ အရေးပေါ် မဟုတ်ပါ။ အလုပ်အတွက် လိုတဲ့ ပျက်နေတဲ့ဖုန်းက ဖြစ်နိုင်ပါတယ်။ သုံးပြီးရင် နောက်လစာမှာ ပြန်ဖြည့်ပါ။
`,
    takeawaysEn: ['A small cushion you keep is better than a perfect plan you do not start.', 'Refill the cushion after you use it.'],
    takeawaysMy: ['မစတဲ့ ပြီးပြည့်စုံတဲ့ အစီအစဉ်ထက် ထိန်းထားတဲ့ အရန်ငယ်က ပိုကောင်းပါတယ်။', 'သုံးပြီးရင် ပြန်ဖြည့်ပါ။'],
    canEn: ['Pick a first savings target under $500', 'Choose an amount to set aside each payday'],
    canMy: ['ဒေါ်လာ ၅၀၀ အောက် ပထမပစ်မှတ် ရွေးနိုင်သည်', 'လစာတိုင်း ခွဲမည့် ပမာဏ ရွေးနိုင်သည်'],
    taskEn: 'Choose a first target amount and the dollar amount you will move on payday.',
    taskMy: 'ပထမပစ်မှတ်နဲ့ လစာထုတ်ရင် ရွှေ့မည့် ဒေါ်လာပမာဏကို ရွေးပါ။',
  }),
];

const healthLessons: ContentLesson[] = [
  reading({
    id: 301,
    pathId: pathIds.health,
    moduleId: moduleIds.healthWords,
    slug: 'insurance-words',
    order: 1,
    duration: '12 min',
    titleEn: 'The four words on every insurance card',
    titleMy: 'အာမခံကတ်ပေါ်က စကားလုံးလေးလုံး',
    skillEn: 'Read a plan well enough to avoid a surprise bill',
    skillMy: 'မထင်မှတ်တဲ့ ဘီလ်ကို ရှောင်နိုင်အောင် အစီအစဉ်ကို ဖတ်နိုင်သည်',
    introEn: 'U.S. health care is expensive without insurance. You need four words, not the whole system.',
    introMy: 'အာမခံမရှိရင် အမေရိကန်ကျန်းမာရေးစောင့်ရှောက်မှု ဈေးကြီးပါတယ်။ စနစ်တစ်ခုလုံး မဟုတ်ဘဲ စကားလုံးလေးလုံး လိုပါတယ်။',
    bodyEn: `## Four words

**Premium** is what you pay every month to keep the plan, even if you do not see a doctor.

**Deductible** is what you pay yourself each year before the plan starts paying for many services.

**Copay** is a fixed amount for a visit, such as $30.

**Network** means the doctors and clinics that have a contract with your plan. In network usually costs less. Out of network can mean you pay most of the bill.

### Before a non-urgent visit

Ask: "Do you take my plan?" and "Is this in network?" Write the answer and the person's name.

If someone may die or be badly hurt, call 911 or go to an emergency room first. Sort out insurance after you are safe.
`,
    bodyMy: `## စကားလုံးလေးလုံး

**Premium** က ဆရာဝန်မပြရင်တောင် အစီအစဉ်ဆက်ထားဖို့ လတိုင်းပေးရတဲ့ ငွေပါ။

**Deductible** က အစီအစဉ်က ဝန်ဆောင်မှုအများစု မပေးခင် တစ်နှစ်အတွင်း ကိုယ်တိုင်ပေးရတဲ့ ငွေပါ။

**Copay** က လာရောက်မှုတစ်ခုအတွက် ပုံသေပမာဏပါ။ ဥပမာ ဒေါ်လာ ၃၀။

**Network** က သင့်အစီအစဉ်နဲ့ စာချုပ်ရှိတဲ့ ဆရာဝန်နဲ့ ဆေးခန်းတွေပါ။ ကွန်ရက်ထဲက ပိုသက်သာတတ်ပါတယ်။ ကွန်ရက်ပြင်ပဆိုရင် ဘီလ်အများစုကို ကိုယ်ပေးရနိုင်ပါတယ်။

### အရေးမကြီးတဲ့ လာရောက်မှုမတိုင်ခင်

"Do you take my plan?" နဲ့ "Is this in network?" လို့ မေးပါ။ အဖြေနဲ့ ပြောတဲ့သူနာမည်ကို ရေးပါ။

တစ်စုံတစ်ယောက် သေနိုင် သို့မဟုတ် ပြင်းပြင်းထန်ထန် ဒဏ်ရာရနိုင်ရင် အာမခံမစဉ်းစားခင် 911 ခေါ်ပါ သို့မဟုတ် အရေးပေါ်ခန်းသွားပါ။
`,
    takeawaysEn: ['The monthly premium does not mean visits are free.', 'In-network care is usually far cheaper. Emergencies still come first.'],
    takeawaysMy: ['လစဉ် premium က လာရောက်မှု အခမဲ့လို့ မဆိုလိုပါ။', 'ကွန်ရက်ထဲက ကုသမှုက ပိုသက်သာတတ်ပါတယ်။ အရေးပေါ်ကို အရင်ထားပါ။'],
    canEn: ['Explain premium, deductible, copay, and network', 'Ask whether a clinic is in network'],
    canMy: ['premium၊ deductible၊ copay၊ network ကို ရှင်းပြနိုင်သည်', 'ဆေးခန်းက ကွန်ရက်ထဲလား မေးနိုင်သည်'],
    taskEn: 'Write the four words and one question you will ask before a clinic visit.',
    taskMy: 'စကားလုံးလေးလုံးနဲ့ ဆေးခန်းမသွားခင် မေးမည့် မေးခွန်းတစ်ခု ရေးပါ။',
  }),
  reading({
    id: 302,
    pathId: pathIds.health,
    moduleId: moduleIds.healthWords,
    slug: 'where-coverage-comes-from',
    order: 2,
    duration: '12 min',
    titleEn: 'Where people actually get a plan',
    titleMy: 'လူတွေ အစီအစဉ်ကို ဘယ်က ရသလဲ',
    skillEn: 'Find the door that matches your situation',
    skillMy: 'သင့်အခြေအနေနဲ့ ကိုက်တဲ့ တံခါးကို ရှာပါ',
    introEn: 'Most people get coverage from a job, a state program, or HealthCare.gov.',
    introMy: 'လူအများစုက အလုပ်၊ ပြည်နယ်အစီအစဉ်၊ သို့မဟုတ် HealthCare.gov ကနေ အာမခံရပါတယ်။',
    bodyEn: `## Which door fits

If an employer offers insurance, ask human resources when you can enroll and what part of the premium you pay. Some jobs make you wait 30 to 90 days.

[HealthCare.gov](https://www.healthcare.gov) is the federal marketplace. Some states use their own site. Open enrollment is once a year, but a move or a job loss can open a special period.

Medicaid and CHIP are state programs for people with lower income, including many children and pregnant people. Rules depend on the state and on immigration status.

Community health centers provide care on a sliding scale if you are uninsured. Bring any ID, a list of medicines, and a way to be contacted.
`,
    bodyMy: `## ဘယ်တံခါး ကိုက်သလဲ

အလုပ်ရှင်က အာမခံပေးရင် လူ့စွမ်းအားဌာနကို ဘယ်တော့ စာရင်းသွင်းနိုင်သလဲ၊ premium ဘယ်လောက်ကို ကိုယ်ပေးရသလဲ မေးပါ။ အလုပ်တချို့က ရက် ၃၀ ကနေ ၉၀ စောင့်ခိုင်းပါတယ်။

[HealthCare.gov](https://www.healthcare.gov) က ဖက်ဒရယ်ဈေးကွက်ပါ။ ပြည်နယ်တချို့က ကိုယ့်ဆိုဒ်သုံးပါတယ်။ ပွင့်လင်းစာရင်းသွင်းချိန်က တစ်နှစ်တစ်ကြိမ်ပါ။ ပြောင်းရွှေ့ခြင်း သို့မဟုတ် အလုပ်ဆုံးရှုံးခြင်းက အထူးကာလ ဖွင့်နိုင်ပါတယ်။

Medicaid နဲ့ CHIP က ဝင်ငွေနည်းသူတွေအတွက် ပြည်နယ်အစီအစဉ်ပါ။ ကလေးအများစုနဲ့ ကိုယ်ဝန်ဆောင်တွေ ပါဝင်တတ်ပါတယ်။ စည်းမျဉ်းက ပြည်နယ်နဲ့ လူဝင်မှုအခြေအနေပေါ် မူတည်ပါတယ်။

အာမခံမရှိရင် ရပ်ရွာကျန်းမာရေးဌာနက ဝင်ငွေအလိုက် လျှော့ဈေးနဲ့ ကုသပေးပါတယ်။ ID၊ ဆေးစာရင်း၊ နှင့် ဆက်သွယ်လို့ရတဲ့ နည်းလမ်း ယူသွားပါ။
`,
    takeawaysEn: ['Ask your employer first if you have a job with benefits.', 'A community health center can bridge a gap. Do not wait on urgent care for a perfect plan.'],
    takeawaysMy: ['ခံစားခွင့်ပါတဲ့ အလုပ်ရှိရင် အလုပ်ရှင်ကို အရင်မေးပါ။', 'ရပ်ရွာကျန်းမာရေးဌာနက ကွာဟချက်ကို ကာနိုင်ပါတယ်။ အရေးကြီးတဲ့ ကုသမှုကို ပြီးပြည့်စုံတဲ့ အစီအစဉ်အတွက် မစောင့်ပါနဲ့။'],
    canEn: ['Name the coverage door that fits your job and family', 'Know that a community health center can see you while you wait'],
    canMy: ['အလုပ်နဲ့ မိသားစုနဲ့ ကိုက်တဲ့ တံခါးကို ခေါ်နိုင်သည်', 'စောင့်နေချိန် ရပ်ရွာကျန်းမာရေးဌာနက ကြည့်နိုင်ကြောင်း သိသည်'],
    taskEn: 'Write which door you will check first: employer, HealthCare.gov, Medicaid, or a community health center.',
    taskMy: 'ပထမစစ်မည့် တံခါးကို ရေးပါ။ အလုပ်ရှင်၊ HealthCare.gov၊ Medicaid၊ သို့မဟုတ် ရပ်ရွာကျန်းမာရေးဌာန။',
  }),
  reading({
    id: 303,
    pathId: pathIds.health,
    moduleId: moduleIds.healthUse,
    slug: 'where-to-go-for-care',
    order: 1,
    duration: '10 min',
    titleEn: 'Clinic, urgent care, or emergency room',
    titleMy: 'ဆေးခန်း၊ အရေးပေါ်ခန်းငယ်၊ သို့မဟုတ် အရေးပေါ်ခန်း',
    skillEn: 'Pick the lowest-cost place that is still safe',
    skillMy: 'လုံခြုံပြီး ဈေးအသက်သာဆုံး နေရာကို ရွေးပါ',
    introEn: 'The emergency room is for emergencies. Using it for a sore throat can cost hundreds or thousands of dollars.',
    introMy: 'အရေးပေါ်ခန်းက အရေးပေါ်အတွက်ပါ။ လည်ချောင်းနာတာအတွက် သုံးရင် ဒေါ်လာ ရာချီ သို့မဟုတ် ထောင်ချီ ကုန်နိုင်ပါတယ်။',
    bodyEn: `## Match the problem to the place

A primary care clinic or community health center is for checkups, ongoing problems, vaccines, and referrals.

Urgent care is for problems that need help today but are not life-threatening: a painful ear, a sprain, or a fever.

The emergency room and 911 are for chest pain, trouble breathing, heavy bleeding, a serious injury, or any time you think a life is in danger.

### Before you are sick

Look up urgent care that is in network and save the address. If a nurse line is on your card, call it when you are unsure.

After the visit, keep the paperwork. If a bill looks wrong, ask for an itemized list before you pay a large amount. Ask if they have financial assistance.
`,
    bodyMy: `## ပြဿနာနဲ့ နေရာကို တွဲပါ

ပထမဆုံးဆေးခန်း သို့မဟုတ် ရပ်ရွာကျန်းမာရေးဌာနက စစ်ဆေးမှု၊ ဆက်ရှိနေတဲ့ ပြဿနာ၊ ကာကွယ်ဆေး၊ နှင့် လွှဲပြောင်းမှုအတွက်ပါ။

အရေးပေါ်ခန်းငယ် (urgent care) က ဒီနေ့လိုအပ်ပေမယ့် အသက်အန္တရာယ်မရှိတဲ့ ပြဿနာအတွက်ပါ။ နားကိုက်၊ လိမ်ခြင်း၊ ဖျားခြင်း။

အရေးပေါ်ခန်းနဲ့ 911 က ရင်ဘတ်အောင့်၊ အသက်ရှူရခက်၊ သွေးအများကြီးထွက်၊ ဒဏ်ရာပြင်း၊ သို့မဟုတ် အသက်အန္တရာယ်ရှိတယ်လို့ ထင်ရင်ပါ။

### မဖျားခင်

ကွန်ရက်ထဲက urgent care လိပ်စာကို ရှာပြီး သိမ်းပါ။ ကတ်ပေါ်မှာ သူနာပြုလိုင်းရှိရင် မသေချာချိန် ခေါ်ပါ။

လာရောက်ပြီးရင် စာရွက်ကို သိမ်းပါ။ ဘီလ်မမှန်ရင် ပမာဏကြီး မပေးခင် အသေးစိတ်စာရင်း တောင်းပါ။ ငွေကြေးအကူအညီ ရှိမရှိ မေးပါ။
`,
    takeawaysEn: ['Emergency rooms are for danger, not for convenience.', 'Look up an in-network urgent care before you are sick.'],
    takeawaysMy: ['အရေးပေါ်ခန်းက အန္တရာယ်အတွက်ပါ။ အဆင်ပြေမှုအတွက် မဟုတ်ပါ။', 'မဖျားခင် ကွန်ရက်ထဲက urgent care ကို ရှာထားပါ။'],
    canEn: ['Match a problem to clinic, urgent care, or emergency care', 'Save one in-network urgent care address'],
    canMy: ['ပြဿနာကို ဆေးခန်း၊ urgent care၊ သို့မဟုတ် အရေးပေါ်နဲ့ တွဲနိုင်သည်', 'ကွန်ရက်ထဲက urgent care လိပ်စာတစ်ခု သိမ်းနိုင်သည်'],
    taskEn: 'Save 911 and one clinic or urgent care address in your phone.',
    taskMy: 'ဖုန်းထဲမှာ 911 နဲ့ ဆေးခန်း သို့မဟုတ် urgent care လိပ်စာတစ်ခု သိမ်းပါ။',
  }),
  reading({
    id: 304,
    pathId: pathIds.health,
    moduleId: moduleIds.healthUse,
    slug: 'appointment-checklist',
    order: 2,
    duration: '8 min',
    titleEn: 'What to bring to an appointment',
    titleMy: 'ချိန်းဆိုမှုသို့ ဘာယူသွားမလဲ',
    skillEn: 'Walk in with the papers and questions you need',
    skillMy: 'လိုတဲ့ စာရွက်နဲ့ မေးခွန်းတွေနဲ့ ဝင်ပါ',
    introEn: 'The front desk will ask who you are, how you will pay, and why you came. A small folder makes this calm.',
    introMy: 'ကောင်တာက သင်ဘယ်သူလဲ၊ ဘယ်လိုပေးမလဲ၊ ဘာကြောင့်လာသလဲ မေးပါမယ်။ ဖိုင်တွဲသေးသေးက ဒါကို တည်ငြိမ်စေပါတယ်။',
    bodyEn: `## Pack a small folder

- Photo ID
- Insurance card if you have one
- A list of medicines and doses
- Records you carried from home
- Two questions written down

Examples: "What is this medicine for?" and "What should I do if I feel worse tonight?"

You can ask the clinic for an interpreter. You have a right to understand your care. If you do not understand, say "Please explain that again more simply."

Ask what the visit will cost if you are uninsured. Sliding-scale clinics can lower the fee based on income.

Leave with the next step written down: a prescription, a follow-up date, or a phone number.
`,
    bodyMy: `## ဖိုင်တွဲသေးသေး ထုပ်ပါ

- ဓာတ်ပုံပါ ID
- ရှိရင် အာမခံကတ်
- ဆေးနဲ့ ပမာဏစာရင်း
- အိမ်က ယူလာတဲ့ မှတ်တမ်း
- ရေးထားတဲ့ မေးခွန်းနှစ်ခု

ဥပမာ "ဒီဆေးက ဘာအတွက်လဲ" နဲ့ "ဒီည ပိုဆိုးရင် ဘာလုပ်ရမလဲ"။

ဆေးခန်းကို စကားပြန် တောင်းနိုင်ပါတယ်။ ကုသမှုကို နားလည်ခွင့် ရှိပါတယ်။ နားမလည်ရင် "Please explain that again more simply." လို့ ပြောပါ။

အာမခံမရှိရင် လာရောက်ခ ဘယ်လောက်လဲ မေးပါ။ လျှော့ဈေးဆေးခန်းက ဝင်ငွေအလိုက် လျှော့နိုင်ပါတယ်။

နောက်တစ်ဆင့်ကို ရေးပြီး ထွက်ပါ။ ဆေးစာ၊ နောက်ချိန်းရက်၊ သို့မဟုတ် ဖုန်းနံပါတ်။
`,
    takeawaysEn: ['A medicine list and two questions change the quality of the visit.', 'You can ask for an interpreter and for simpler words.'],
    takeawaysMy: ['ဆေးစာရင်းနဲ့ မေးခွန်းနှစ်ခုက လာရောက်မှုအရည်အသွေးကို ပြောင်းပါတယ်။', 'စကားပြန်နဲ့ ပိုရိုးရှင်းတဲ့ စကားလုံး တောင်းနိုင်ပါတယ်။'],
    canEn: ['Pack ID, a medicine list, and two questions', 'Ask for an interpreter or a simpler explanation'],
    canMy: ['ID၊ ဆေးစာရင်း၊ မေးခွန်းနှစ်ခု ယူသွားနိုင်သည်', 'စကားပြန် သို့မဟုတ် ပိုရိုးရှင်းတဲ့ ရှင်းပြချက် တောင်းနိုင်သည်'],
    taskEn: 'Make a note with your medicines and two questions you would ask a doctor.',
    taskMy: 'ဆေးတွေနဲ့ ဆရာဝန်ကို မေးမည့် မေးခွန်းနှစ်ခု မှတ်စုလုပ်ပါ။',
  }),
];

const dmvModuleId = seedId(41);
const dmvSignsId = seedId(42);
const dmvShareId = seedId(43);
const dmvGradId = seedId(44);
const dmvPathId = seedId(9);

const rightOfWaySections: LessonSection[] = [
  {
    heading_en: 'Who goes first',
    heading_my: 'ဘယ်သူ အရင်သွားသလဲ',
    body_en: 'At an all-way stop, the first car to arrive goes first. If two cars arrive together, yield to the car on your right.',
    body_my: 'အဖက်အားလုံး ရပ်တဲ့ လမ်းဆုံမှာ အရင်ရောက်တဲ့ ကားက အရင်သွားပါတယ်။ နှစ်စီး တစ်ပြိုင်နက်ရောက်ရင် ညာဘက်က ကားကို လမ်းပေးပါ။',
    diagram: allWayTogether,
    caption_en: 'A and B arrived together. B is on A’s right, so B goes first.',
    caption_my: 'A နဲ့ B တစ်ပြိုင်နက်ရောက်တယ်။ B က A ရဲ့ ညာဘက်မှာမို့ B အရင်သွားတယ်။',
  },
  {
    heading_en: 'Stop before the crosswalk',
    heading_my: 'လူကူးမတိုင်ခင် ရပ်ပါ',
    body_en: 'Stop for a pedestrian in a crosswalk. Stop before the crosswalk, not in it. Do not pass a car that has stopped for a pedestrian.',
    body_my: 'လူကူးမျဉ်းကျားထဲက လူအတွက် ရပ်ပါ။ လူကူးထဲမှာ မရပ်ဘဲ မျဉ်းမတိုင်ခင် ရပ်ပါ။ လမ်းလျှောက်သူအတွက် ရပ်ထားတဲ့ ကားကို မကျော်ပါနဲ့။',
    diagram: doNotPass,
    caption_en: 'A has stopped before the crosswalk. B does not pass A.',
    caption_my: 'A က လူကူးမတိုင်ခင် ရပ်ထားတယ်။ B က A ကို မကျော်ဘူး။',
  },
  {
    heading_en: 'A yield sign',
    heading_my: 'Yield ဆိုင်းဘုတ်',
    body_en: 'A yield sign means slow down and give the road to cross traffic. Go only when it is safe.',
    body_my: 'Yield ဆိုင်းဘုတ်က အရှိန်လျှော့ပြီး ဖြတ်သွားတဲ့ ကားကို လမ်းပေးရမယ်လို့ ဆိုလိုပါတယ်။ ဘေးကင်းမှ သွားပါ။',
    diagram: yieldSign,
    caption_en: 'Slow down at a yield sign and let the other traffic go.',
    caption_my: 'Yield ဆိုင်းမှာ အရှိန်လျှော့ပြီး တစ်ခြားကားကို လမ်းပေးပါ။',
  },
];

const wordCheckSections: LessonSection[] = [
  {
    heading_en: 'The words on the sign',
    heading_my: 'ဆိုင်းပေါ်က စကားလုံး',
    body_en: 'Yield means give the road to other traffic. A pedestrian is a person on foot. A crosswalk is the marked place they cross.',
    body_my: 'Yield ဆိုတာ တစ်ခြားကားကို လမ်းပေးတာပါ။ Pedestrian ဆိုတာ လမ်းလျှောက်သူပါ။ Crosswalk ဆိုတာ သူတို့ကူးတဲ့ မျဉ်းကျားပါ။',
    diagram: crosswalkStop,
    caption_en: 'A stops before the crosswalk and waits for the person on foot.',
    caption_my: 'A က လူကူးမတိုင်ခင် ရပ်ပြီး လမ်းလျှောက်သူကို စောင့်တယ်။',
  },
];

const signSections: LessonSection[] = [
  {
    heading_en: 'Octagon',
    heading_my: 'ရှစ်မြောင့်',
    body_en: 'A stop sign is an octagon. Come to a full stop, then look.',
    body_my: 'ရပ်ဆိုင်းက ရှစ်မြောင့်ပါ။ အပြည့်ရပ်ပြီးမှ ကြည့်ပါ။',
    diagram: stopSign,
    caption_en: 'An eight-sided sign means stop.',
    caption_my: 'ရှစ်မြောင့်ဆိုင်းက ရပ်ပါလို့ ဆိုလိုတယ်။',
  },
  {
    heading_en: 'Upside-down triangle',
    heading_my: 'ပြောင်းပြန်တြိဂံ',
    body_en: 'A yield sign is an upside-down triangle. Slow down and let the other traffic go.',
    body_my: 'Yield ဆိုင်းက ပြောင်းပြန်တြိဂံပါ။ အရှိန်လျှော့ပြီး တစ်ခြားကားကို လမ်းပေးပါ။',
    diagram: yieldSign,
    caption_en: 'An upside-down triangle means yield.',
    caption_my: 'ပြောင်းပြန်တြိဂံက yield ပါ။',
  },
  {
    heading_en: 'Yellow diamond',
    heading_my: 'အဝါရောင် စိန်ပုံ',
    body_en: 'Most warning signs are yellow and diamond-shaped. They tell you about a condition ahead. They are not a stop sign.',
    body_my: 'သတိပေးဆိုင်း အများစုက အဝါရောင် စိန်ပုံပါ။ ရှေ့က အခြေအနေကို ပြောပါတယ်။ ရပ်ဆိုင်းနဲ့ မတူပါ။',
    diagram: warningSign,
    caption_en: 'Most warning signs are yellow diamonds.',
    caption_my: 'သတိပေးဆိုင်း အများစုက အဝါရောင် စိန်ပုံပါ။',
  },
];

const sharingSections: LessonSection[] = [
  {
    heading_en: 'Left turn',
    heading_my: 'ဘယ်ကွေ့',
    body_en: 'A left turn yields to oncoming traffic that is close enough to be a hazard.',
    body_my: 'ဘယ်ကွေ့က အန္တရာယ်ဖြစ်လောက်အောင် နီးတဲ့ မျက်နှာချင်းဆိုင်ကားကို လမ်းပေးရပါတယ်။',
    diagram: leftTurn,
    caption_en: 'A is turning left. B is close, so A waits.',
    caption_my: 'A က ဘယ်ကွေ့မယ်။ B က နီးလို့ A စောင့်တယ်။',
  },
  {
    heading_en: 'School bus',
    heading_my: 'ကျောင်းကား',
    body_en: 'Stop for a school bus with flashing red lights from both directions when the road has no median.',
    body_my: 'ခြားလျှင်မရှိတဲ့ လမ်းမှာ ကျောင်းကားအနီမီးအတွက် နှစ်ဖက်လုံး ရပ်ပါ။',
    diagram: schoolBus,
    caption_en: 'Red lights are flashing and there is no median. B and C stop.',
    caption_my: 'အနီမီးတဖျတ်ဖျတ်၊ ခြားလျှင်မရှိ။ B နဲ့ C ရပ်တယ်။',
  },
  {
    heading_en: 'Divided highway',
    heading_my: 'ခြားထားတဲ့လမ်း',
    body_en: 'You do not stop when you are on the opposite side of a divided highway.',
    body_my: 'ခြားထားတဲ့ အဝေးပြေးလမ်း တစ်ဖက်မှာ ဆိုရင် ရပ်စရာမလိုပါ။',
    diagram: dividedBus,
    caption_en: 'A median separates the road. C does not stop for the bus.',
    caption_my: 'ခြားလျှင်က လမ်းကို ခွဲထားတယ်။ C က ကျောင်းကားအတွက် မရပ်ပါ။',
  },
  {
    heading_en: 'Roundabout',
    heading_my: 'အဝိုင်းလမ်း',
    body_en: 'Yield to traffic already in the roundabout. Enter when there is a safe gap. Do not stop once you are inside the circle.',
    body_my: 'အဝိုင်းထဲ ရောက်နှင့်ပြီးသား ကားကို လမ်းပေးပါ။ ဟင်းလင်း ဘေးကင်းမှ ဝင်ပါ။ အဝိုင်းထဲ ရောက်ပြီးရင် မရပ်ပါနဲ့။',
    diagram: roundaboutYield,
    caption_en: 'B is already in the roundabout. A yields.',
    caption_my: 'B က အဝိုင်းထဲ ရောက်နှင့်ပြီ။ A က လမ်းပေးတယ်။',
  },
  {
    heading_en: 'Headlights in the rain',
    heading_my: 'မိုးထဲက ရှေ့မီး',
    body_en: 'In California, turn on your headlights when the windshield wipers are in continuous use.',
    body_my: 'ကယ်လီဖိုးနီးယားမှာ ရေသုတ်စက်ကို ဆက်တိုက်သုံးရင် ရှေ့မီး ဖွင့်ပါ။',
    diagram: headlights,
    caption_en: 'The wipers are on, so A turns the headlights on.',
    caption_my: 'ရေသုတ်စက် ဖွင့်ထားလို့ A က ရှေ့မီး ဖွင့်တယ်။',
  },
];

const dmvLessons: ContentLesson[] = [
  reading({
    id: 401,
    pathId: dmvPathId,
    moduleId: dmvModuleId,
    slug: 'right-of-way',
    order: 1,
    duration: '12 min',
    titleEn: 'Right-of-way at an intersection',
    titleMy: 'လမ်းဆုံတွင် လမ်းဦးစားပေး',
    skillEn: 'Say who goes first, in the words the written test uses',
    skillMy: 'စာမေးပွဲသုံးစကားလုံးနဲ့ ဘယ်သူအရင်သွားရမလဲ ပြောနိုင်သည်',
    introEn: 'The California written test is in English. Learn the words, then the rule.',
    introMy: 'ကယ်လီဖိုးနီးယား စာမေးပွဲက အင်္ဂလိပ်လိုပါ။ စကားလုံးကို အရင်သိပြီး စည်းမျဉ်းကို သိပါ။',
    bodyEn: `## Who goes first

At an all-way stop, the first car to arrive goes first. If two cars arrive together, yield to the car on your right.

### People on foot

- Stop for a pedestrian in a crosswalk
- Do not pass a vehicle that has stopped for a pedestrian

A yield sign means slow down and give the road to cross traffic. You go only when it is safe.
`,
    bodyMy: `## ဘယ်သူ အရင်သွားသလဲ

အဖက်အားလုံး ရပ်တဲ့ လမ်းဆုံမှာ အရင်ရောက်တဲ့ ကားက အရင်သွားပါတယ်။ နှစ်စီး တစ်ပြိုင်နက်ရောက်ရင် ညာဘက်က ကားကို လမ်းလွှဲပါ။

### လမ်းလျှောက်သူ

- လူကူးမျဉ်းကျားထဲက လူအတွက် ရပ်ပါ
- လမ်းလျှောက်သူအတွက် ရပ်ထားတဲ့ ကားကို မကျော်ပါနဲ့

Yield ဆိုင်းဘုတ်က အရှိန်လျှော့ပြီး ဖြတ်သွားတဲ့ ကားကို လမ်းပေးရမယ်လို့ ဆိုလိုပါတယ်။ ဘေးကင်းမှ သွားပါ။
`,
    takeawaysEn: ['First to arrive goes first at an all-way stop.', 'Yield means let the other traffic go.'],
    takeawaysMy: ['အဖက်အားလုံး ရပ်ရင် အရင်ရောက်သူ အရင်သွားသည်။', 'Yield ဆိုတာ တစ်ခြားကားကို လမ်းပေးခြင်းပါ။'],
    canEn: ['Explain right-of-way when two cars arrive together'],
    canMy: ['ကားနှစ်စီး တစ်ပြိုင်နက်ရောက်ရင် လမ်းဦးစားပေးကို ရှင်းပြနိုင်သည်'],
    taskEn: 'Write one sentence, in English, about who you yield to at an all-way stop.',
    taskMy: 'အဖက်အားလုံးရပ်တဲ့နေရာမှာ ဘယ်သူ့ကို လမ်းပေးရမလဲ အင်္ဂလိပ်လို စာကြောင်းတစ်ကြောင်း ရေးပါ။',
    cards: [
      {
        image_url: '/images/card-stop.svg',
        diagram: stopSign,
        caption_en: 'An eight-sided sign means stop.',
        caption_my: 'ရှစ်မြောင့်ဆိုင်းက ရပ်ပါလို့ ဆိုလိုတယ်။',
        lines_en: ['A stop sign means a full stop.', 'Stop behind the limit line, crosswalk, or intersection.', 'Look left, then right, then left again.'],
        lines_my: ['ရပ်ဆိုင်းဘုတ်က အပြည့်ရပ်ရမယ်လို့ ဆိုလိုပါတယ်။', 'မျဉ်း၊ လူကူး၊ သို့မဟုတ် လမ်းဆုံရှေ့မှာ ရပ်ပါ။', 'ဘယ်၊ ညာ၊ ပြီးရင် ဘယ်ကို ပြန်ကြည့်ပါ။'],
        key_en: 'Full stop, then look.',
        key_my: 'အပြည့်ရပ်ပြီးမှ ကြည့်ပါ။',
        takeaway_en: 'If the wheels are still rolling, you have not stopped.',
        takeaway_my: 'ဘီးလှိမ့်နေရင် ရပ်ပြီးသေးတာ မဟုတ်ပါ။',
      },
      {
        image_url: '/images/card-yield.svg',
        diagram: yieldSign,
        caption_en: 'Slow down at a yield sign and let the other traffic go.',
        caption_my: 'Yield ဆိုင်းမှာ အရှိန်လျှော့ပြီး တစ်ခြားကားကို လမ်းပေးပါ။',
        lines_en: ['A yield sign means give the road to other traffic.', 'Slow down and be ready to stop.', 'Go only when the way is clear.'],
        lines_my: ['Yield ဆိုင်းဘုတ်က တစ်ခြားကားကို လမ်းပေးရမယ်လို့ ဆိုလိုပါတယ်။', 'အရှိန်လျှော့ပြီး ရပ်ဖို့ အသင့်နေပါ။', 'လမ်းရှင်းမှ သွားပါ။'],
        key_en: 'Slow down and let the other traffic go.',
        key_my: 'အရှိန်လျှော့ပြီး တစ်ခြားကားကို လမ်းပေးပါ။',
        takeaway_en: 'Yield means the other road goes first.',
        takeaway_my: 'Yield ဆိုတာ တစ်ခြားလမ်းက အရင်သွားတာပါ။',
      },
      {
        image_url: '/images/card-crosswalk.svg',
        diagram: doNotPass,
        caption_en: 'A has stopped before the crosswalk. B does not pass A.',
        caption_my: 'A က လူကူးမတိုင်ခင် ရပ်ထားတယ်။ B က A ကို မကျော်ဘူး။',
        lines_en: ['Stop for a pedestrian in a crosswalk.', 'Wait until they have crossed.', 'Do not pass a car that has stopped for a pedestrian.'],
        lines_my: ['လူကူးမျဉ်းကျားထဲက လူအတွက် ရပ်ပါ။', 'သူကူးပြီးမှ သွားပါ။', 'လမ်းလျှောက်သူအတွက် ရပ်ထားတဲ့ ကားကို မကျော်ပါနဲ့။'],
        key_en: 'People in the crosswalk go first.',
        key_my: 'လူကူးထဲက လူက အရင်ပါ။',
        takeaway_en: 'A stopped car at a crosswalk is a stop for you too.',
        takeaway_my: 'လူကူးမှာ ရပ်ထားတဲ့ ကားက သင့်အတွက်လည်း ရပ်ခိုင်းတာပါ။',
      },
      {
        lines_en: ['A arrives at an all-way stop.', 'The first car to arrive goes first.', 'Stop completely, then look.'],
        lines_my: ['A က အဖက်အားလုံးရပ်တဲ့ လမ်းဆုံကို ရောက်တယ်။', 'အရင်ရောက်တဲ့ ကားက အရင်သွားပါတယ်။', 'အပြည့်ရပ်ပြီးမှ ကြည့်ပါ။'],
        key_en: 'First to arrive goes first.',
        key_my: 'အရင်ရောက်သူ အရင်သွားသည်။',
        takeaway_en: 'A is alone at the intersection.',
        takeaway_my: 'လမ်းဆုံမှာ A တစ်စီးတည်း ရှိတယ်။',
        diagram: allWayStep(['car-a']),
        caption_en: 'A has arrived. No other car is in the intersection yet.',
        caption_my: 'A ရောက်ပြီးပြီ။ လမ်းဆုံထဲမှာ တစ်ခြားကား မရှိသေးပါ။',
      },
      {
        lines_en: ['B arrives at the same time.', 'Same-time arrivals do not guess who is bigger.', 'Look for the car on your right.'],
        lines_my: ['B က တစ်ပြိုင်နက် ရောက်တယ်။', 'တစ်ပြိုင်နက်ရောက်ရင် ဘယ်ကားပိုကြီးလဲ မမှန်းပါနဲ့။', 'ညာဘက်က ကားကို ကြည့်ပါ။'],
        key_en: 'Two cars can arrive together.',
        key_my: 'ကားနှစ်စီး တစ်ပြိုင်နက် ရောက်နိုင်တယ်။',
        takeaway_en: 'B is now in the picture with A.',
        takeaway_my: 'B က A နဲ့အတူ ပုံထဲရောက်ပြီ။',
        diagram: allWayStep(['car-b']),
        caption_en: 'B arrives while A is still waiting.',
        caption_my: 'A စောင့်နေတုန်း B ရောက်တယ်။',
      },
      {
        lines_en: ['B is on A’s right, so B goes first.', 'Yield to the car on your right.', 'Then go when it is your turn.'],
        lines_my: ['B က A ရဲ့ ညာဘက်မှာမို့ B အရင်သွားတယ်။', 'ညာဘက်က ကားကို လမ်းပေးပါ။', 'သင့်အလှည့်ရောက်မှ သွားပါ။'],
        key_en: 'The car on the right goes first.',
        key_my: 'ညာဘက်က ကားက အရင်သွားတယ်။',
        takeaway_en: 'Same-time arrivals yield to the right.',
        takeaway_my: 'တစ်ပြိုင်နက်ရောက်ရင် ညာဘက်ကို လမ်းပေးပါ။',
        diagram: allWayStep(['who']),
        caption_en: 'A and B arrived together. B is on A’s right, so B goes first.',
        caption_my: 'A နဲ့ B တစ်ပြိုင်နက်ရောက်တယ်။ B က A ရဲ့ ညာဘက်မှာမို့ B အရင်သွားတယ်။',
      },
    ],
    practiceTopic: 'right-of-way',
    sections: rightOfWaySections,
  }),
  reading({
    id: 402,
    pathId: dmvPathId,
    moduleId: dmvModuleId,
    slug: 'dmv-word-check',
    order: 2,
    duration: '8 min',
    type: 'quiz',
    titleEn: 'Check the English words',
    titleMy: 'အင်္ဂလိပ်စကားလုံး စစ်ဆေးချက်',
    skillEn: 'Match the test words to the rule',
    skillMy: 'စာမေးပွဲစကားလုံးကို စည်းမျဉ်းနဲ့ တွဲနိုင်သည်',
    introEn: 'Answer in English. Open the Burmese only when you need it.',
    introMy: 'အင်္ဂလိပ်လို ဖြေပါ။ လိုမှ မြန်မာစာကို ဖွင့်ပါ။',
    bodyEn: `## This check stays in English

The DMV test will not show a translation. Try the question first. Use Show Burmese if a word is new, then answer again in English.

### Words in this check

- pedestrian
- yield
- crosswalk
`,
    bodyMy: `## ဒီစစ်ဆေးချက်က အင်္ဂလိပ်လိုပါ

DMV စာမေးပွဲမှာ ဘာသာပြန် မပါပါ။ မေးခွန်းကို အရင်ကြိုးစားပါ။ စကားလုံးအသစ်ဆိုရင် မြန်မာပြပါကို ဖွင့်ပြီး အင်္ဂလိပ်လို ပြန်ဖြေပါ။

### ဒီစစ်ဆေးချက်ထဲက စကားလုံး

- pedestrian
- yield
- crosswalk
`,
    takeawaysEn: ['Practice the English word, not only the translation.'],
    takeawaysMy: ['ဘာသာပြန်ချည်းမက အင်္ဂလိပ်စကားလုံးကို လေ့ကျင့်ပါ။'],
    canEn: ['Answer two right-of-way questions in English'],
    canMy: ['လမ်းဦးစားပေးမေးခွန်းနှစ်ခုကို အင်္ဂလိပ်လို ဖြေနိုင်သည်'],
    taskEn: 'Say the words pedestrian, yield, and crosswalk out loud.',
    taskMy: 'pedestrian၊ yield၊ crosswalk ကို အသံထွက် ပြောပါ။',
    sections: wordCheckSections,
    quiz: {
      title_en: 'DMV word check',
      title_my: 'DMV စကားလုံးစစ်ဆေးချက်',
      slug: 'dmv-word-check',
      questions: [
        {
          question_en: 'Two cars arrive at an all-way stop at the same time. Who goes first?',
          question_my: 'အဖက်အားလုံးရပ်တဲ့ လမ်းဆုံကို ကားနှစ်စီး တစ်ပြိုင်နက်ရောက်ရင် ဘယ်သူအရင်သွားသလဲ။',
          options_en: [
            'The larger car',
            'The car on the right',
            'The car going straight',
            'Whoever honks first',
          ],
          options_my: [
            'ကားပိုကြီးတာ',
            'ညာဘက်က ကား',
            'တည့်တည့်သွားမည့် ကား',
            'အရင်ဟွန်းတီးသူ',
          ],
          correct_answer: 1,
          explanation_en: 'When two vehicles arrive together, yield to the vehicle on your right.',
          explanation_my: 'ကားနှစ်စီး တစ်ပြိုင်နက်ရောက်ရင် ညာဘက်က ကားကို လမ်းပေးရပါတယ်။',
        },
        {
          question_en: 'What should you do when a pedestrian is in a crosswalk?',
          question_my: 'လူကူးမျဉ်းကျားထဲမှာ လမ်းလျှောက်သူရှိရင် ဘာလုပ်ရမလဲ။',
          options_en: ['Speed up to pass', 'Honk and continue', 'Stop and wait', 'Change lanes around them'],
          options_my: ['အရှိန်တင်ပြီး ကျော်ပါ', 'ဟွန်းတီးပြီး ဆက်သွားပါ', 'ရပ်ပြီး စောင့်ပါ', 'လမ်းကြောင်းပြောင်း ကျော်ပါ'],
          correct_answer: 2,
          explanation_en: 'Stop for a pedestrian in a crosswalk. Do not pass a car that has stopped for one.',
          explanation_my: 'လူကူးမျဉ်းကျားထဲက လူအတွက် ရပ်ပါ။ လူအတွက် ရပ်ထားတဲ့ ကားကို မကျော်ပါနဲ့။',
        },
      ],
    },
  }),
];

const dmvSignLessons: ContentLesson[] = [
  reading({
    id: 403,
    pathId: dmvPathId,
    moduleId: dmvSignsId,
    slug: 'road-signs',
    order: 1,
    duration: '10 min',
    titleEn: 'Sign shapes',
    titleMy: 'ဆိုင်းဘုတ် ပုံသဏ္ဌာန်',
    skillEn: 'Name a stop sign, a yield sign, and a warning sign from the shape',
    skillMy: 'ပုံသဏ္ဌာန်ကနေ ရပ်၊ yield၊ သတိပေးဆိုင်းကို ခွဲနိုင်သည်',
    introEn: 'Shape tells you the kind of sign before you finish reading the words.',
    introMy: 'စာမကုန်ခင် ပုံသဏ္ဌာန်က ဆိုင်းဘုတ်အမျိုးအစားကို ပြပါတယ်။',
    bodyEn: `## Shapes on the test

An octagon means stop. Come to a full stop, then look.

An upside-down triangle means yield. Slow down and let the other traffic go.

### Warning signs

Most warning signs are yellow and diamond-shaped. They tell you about a condition ahead. They are not the same as a stop sign.
`,
    bodyMy: `## စာမေးပွဲပေါ်က ပုံများ

ရှစ်မြောင့်က ရပ်ပါလို့ ဆိုလိုပါတယ်။ အပြည့်ရပ်ပြီးမှ ကြည့်ပါ။

ပြောင်းပြန်တြိဂံက yield ပါ။ အရှိန်လျှော့ပြီး တစ်ခြားကားကို လမ်းပေးပါ။

### သတိပေးဆိုင်း

သတိပေးဆိုင်း အများစုက အဝါရောင် စိန်ပုံပါ။ ရှေ့က အခြေအနေကို ပြောပါတယ်။ ရပ်ဆိုင်းနဲ့ မတူပါ။
`,
    takeawaysEn: ['Octagon means stop.', 'Upside-down triangle means yield.', 'A diamond usually warns you.'],
    takeawaysMy: ['ရှစ်မြောင့်က ရပ်ပါ။', 'ပြောင်းပြန်တြိဂံက yield ပါ။', 'စိန်ပုံက သတိပေးတတ်ပါတယ်။'],
    canEn: ['Match three sign shapes to their meaning'],
    canMy: ['ဆိုင်းပုံသဏ္ဌာန် သုံးခုကို အဓိပ္ပာယ်နဲ့ တွဲနိုင်သည်'],
    taskEn: 'Point to a stop sign and say the word stop out loud.',
    taskMy: 'ရပ်ဆိုင်းကို ညွှန်ပြီး stop လို့ အသံထွက်ပြောပါ။',
    cards: [
      {
        image_url: '/images/card-stop.svg',
        lines_en: ['A stop sign is an eight-sided octagon.', 'The word on it is STOP.', 'The shape means a full stop even before you read it.'],
        lines_my: ['ရပ်ဆိုင်းက ရှစ်မြောင့်ပါ။', 'အပေါ်က စာလုံးက STOP ပါ။', 'စာမဖတ်ခင်ကတည်းက ပုံက အပြည့်ရပ်ခိုင်းပါတယ်။'],
        key_en: 'Octagon means stop.',
        key_my: 'ရှစ်မြောင့်က ရပ်ပါ။',
        takeaway_en: 'Stop completely, then look.',
        takeaway_my: 'အပြည့်ရပ်ပြီးမှ ကြည့်ပါ။',
      },
      {
        image_url: '/images/card-yield.svg',
        lines_en: ['A yield sign is an upside-down triangle.', 'Slow down and be ready to stop.', 'Cross traffic goes before you.'],
        lines_my: ['Yield ဆိုင်းက ပြောင်းပြန်တြိဂံပါ။', 'အရှိန်လျှော့ပြီး ရပ်ဖို့ အသင့်နေပါ။', 'ဖြတ်သွားတဲ့ ကားက သင့်ထက် အရင်ပါ။'],
        key_en: 'Triangle point-down means yield.',
        key_my: 'အောက်ချွန်တြိဂံက yield ပါ။',
        takeaway_en: 'Be ready to stop at a yield sign.',
        takeaway_my: 'Yield မှာ ရပ်ဖို့ အသင့်နေပါ။',
      },
      {
        lines_en: ['Most warning signs are yellow diamonds.', 'They describe a condition ahead, such as a curve or a crossing.', 'A warning sign is not a stop sign.'],
        lines_my: ['သတိပေးဆိုင်း အများစုက အဝါရောင် စိန်ပုံပါ။', 'ကွေ့လမ်း၊ လူကူးလို ရှေ့က အခြေအနေကို ပြောပါတယ်။', 'သတိပေးဆိုင်းက ရပ်ဆိုင်း မဟုတ်ပါ။'],
        key_en: 'A diamond usually means warning.',
        key_my: 'စိန်ပုံက သတိပေးတတ်ပါတယ်။',
        takeaway_en: 'Read the warning, then adjust your speed.',
        takeaway_my: 'သတိပေးစာကို ဖတ်ပြီး အရှိန်ညှိပါ။',
        diagram: warningSign,
        caption_en: 'Most warning signs are yellow diamonds.',
        caption_my: 'သတိပေးဆိုင်း အများစုက အဝါရောင် စိန်ပုံပါ။',
      },
    ],
    practiceTopic: 'signs',
    sections: signSections,
  }),
];

const dmvShareLessons: ContentLesson[] = [
  reading({
    id: 404,
    pathId: dmvPathId,
    moduleId: dmvShareId,
    slug: 'sharing-the-road',
    order: 1,
    duration: '12 min',
    titleEn: 'Sharing the road',
    titleMy: 'လမ်းကို မျှဝေခြင်း',
    skillEn: 'Yield at a left turn, a school bus, and a roundabout',
    skillMy: 'ဘယ်ကွေ့၊ ကျောင်းကား၊ အဝိုင်းလမ်းမှာ လမ်းပေးနိုင်သည်',
    introEn: 'These rules are about the people who are already using the road.',
    introMy: 'ဒီစည်းတွေက လမ်းကို သုံးနေပြီးသားသူတွေအကြောင်းပါ။',
    bodyEn: `## Who already has the road

A left turn yields to oncoming traffic that is close enough to be a hazard.

Stop for a school bus with flashing red lights from both directions, unless you are on the opposite side of a divided highway.

### Circles and rain

Yield to traffic already in a roundabout. In California, turn on your headlights when the wipers are in continuous use.
`,
    bodyMy: `## လမ်းကို ဘယ်သူ သုံးနေပြီးသားလဲ

ဘယ်ကွေ့က အန္တရာယ်ဖြစ်လောက်အောင် နီးတဲ့ မျက်နှာချင်းဆိုင်ကားကို လမ်းပေးရပါတယ်။

ခြားမထားတဲ့ လမ်းမှာ ကျောင်းကားအနီမီးအတွက် နှစ်ဖက်လုံး ရပ်ပါ။ ခြားထားတဲ့ အဝေးပြေးလမ်း တစ်ဖက်မှာ ဆိုရင် ရပ်စရာမလိုပါ။

### အဝိုင်းနဲ့ မိုး

အဝိုင်းထဲ ရောက်နှင့်ပြီးသား ကားကို လမ်းပေးပါ။ ကယ်လီဖိုးနီးယားမှာ ရေသုတ်စက် ဆက်တိုက်ဖွင့်ရင် ရှေ့မီး ဖွင့်ပါ။
`,
    takeawaysEn: ['Left turns yield to close oncoming cars.', 'A school bus with red lights stops both directions on an undivided road.', 'Traffic already in a roundabout goes first.'],
    takeawaysMy: ['ဘယ်ကွေ့က နီးတဲ့ မျက်နှာချင်းဆိုင်ကားကို လမ်းပေးသည်။', 'ခြားမထားတဲ့လမ်းမှာ ကျောင်းကားအနီမီးအတွက် နှစ်ဖက်လုံး ရပ်သည်။', 'အဝိုင်းထဲက ကားက အရင်သွားသည်။'],
    canEn: ['Explain the school-bus rule and the roundabout rule'],
    canMy: ['ကျောင်းကားစည်းနဲ့ အဝိုင်းလမ်းစည်းကို ရှင်းပြနိုင်သည်'],
    taskEn: 'Say one sentence about when you turn on headlights in the rain.',
    taskMy: 'မိုးရွာရင် ရှေ့မီး ဘယ်တော့ဖွင့်သလဲ စာကြောင်းတစ်ကြောင်း ပြောပါ။',
    cards: [
      {
        lines_en: ['You are turning left across oncoming traffic.', 'If an oncoming car is close, wait.', 'A left turn yields to traffic that is close enough to be a hazard.'],
        lines_my: ['မျက်နှာချင်းဆိုင်ကားကို ဖြတ်ပြီး ဘယ်ကွေ့မယ်။', 'မျက်နှာချင်းဆိုင်ကား နီးရင် စောင့်ပါ။', 'ဘယ်ကွေ့က အန္တရာယ်ဖြစ်လောက်အောင် နီးတဲ့ ကားကို လမ်းပေးရပါတယ်။'],
        key_en: 'A left turn waits for close oncoming traffic.',
        key_my: 'ဘယ်ကွေ့က နီးတဲ့ မျက်နှာချင်းဆိုင်ကားကို စောင့်ရသည်။',
        takeaway_en: 'Do not turn in front of a close oncoming car.',
        takeaway_my: 'နီးတဲ့ မျက်နှာချင်းဆိုင်ကားရှေ့ မကွေ့ပါနဲ့။',
      },
      {
        lines_en: ['A school bus has flashing red lights.', 'On a road with no median, stop from either direction.', 'You do not stop when you are on the opposite side of a divided highway.'],
        lines_my: ['ကျောင်းကားက အနီမီး တဖျတ်ဖျတ်လင်းနေသည်။', 'ခြားလျှင်မရှိတဲ့ လမ်းမှာ နှစ်ဖက်လုံး ရပ်ပါ။', 'ခြားထားတဲ့ အဝေးပြေးလမ်း တစ်ဖက်မှာ ရပ်စရာမလိုပါ။'],
        key_en: 'Red lights on a school bus stop both directions.',
        key_my: 'ကျောင်းကားအနီမီးက နှစ်ဖက်လုံးကို ရပ်ခိုင်းသည်။',
        takeaway_en: 'A divided highway is the exception, not the usual road.',
        takeaway_my: 'ခြားထားတဲ့ အဝေးပြေးလမ်းက ခြွင်းချက်ပါ။ ပုံမှန်လမ်း မဟုတ်ပါ။',
      },
      {
        image_url: '/images/card-roundabout.svg',
        diagram: roundaboutYield,
        caption_en: 'B is already in the roundabout. A yields.',
        caption_my: 'B က အဝိုင်းထဲ ရောက်နှင့်ပြီ။ A က လမ်းပေးတယ်။',
        lines_en: ['Cars already in the roundabout have the road.', 'Yield and enter when there is a gap.', 'Do not stop once you are inside the circle.'],
        lines_my: ['အဝိုင်းထဲ ရောက်နှင့်ပြီးသား ကားက လမ်းပိုင်ပါတယ်။', 'ဟင်းလင်းရှိမှ ဝင်ပါ။', 'အဝိုင်းထဲ ရောက်ပြီးရင် မရပ်ပါနဲ့။'],
        key_en: 'Yield to traffic already in the roundabout.',
        key_my: 'အဝိုင်းထဲက ကားကို လမ်းပေးပါ။',
        takeaway_en: 'Entering traffic waits. Circling traffic goes.',
        takeaway_my: 'ဝင်မည့်ကားက စောင့်သည်။ လည်နေတဲ့ ကားက သွားသည်။',
      },
      {
        lines_en: ['Rain is heavy enough that the wipers stay on.', 'In California, turn on your headlights then.', 'Headlights help other drivers see you.'],
        lines_my: ['မိုးများလို့ ရေသုတ်စက် ဆက်ဖွင့်ထားရသည်။', 'ကယ်လီဖိုးနီးယားမှာ အဲဒီအခါ ရှေ့မီး ဖွင့်ပါ။', 'ရှေ့မီးက တစ်ခြားသူ မြင်အောင် ကူပါတယ်။'],
        key_en: 'Continuous wipers mean headlights on.',
        key_my: 'ရေသုတ်စက် ဆက်တိုက်ဖွင့်ရင် ရှေ့မီး ဖွင့်ပါ။',
        takeaway_en: 'Wipers in continuous use are a headlight rule in California.',
        takeaway_my: 'ကယ်လီဖိုးနီးယားမှာ ရေသုတ်စက် ဆက်တိုက်သုံးရင် ရှေ့မီးစည်း ဖြစ်ပါတယ်။',
        diagram: headlights,
        caption_en: 'The wipers are on, so A turns the headlights on.',
        caption_my: 'ရေသုတ်စက် ဖွင့်ထားလို့ A က ရှေ့မီး ဖွင့်တယ်။',
      },
    ],
    practiceTopic: 'sharing-the-road',
    sections: sharingSections,
  }),
];

const dmvGradLessons: ContentLesson[] = [
  reading({
    id: 405,
    pathId: dmvPathId,
    moduleId: dmvGradId,
    slug: 'test-day',
    order: 1,
    duration: '8 min',
    titleEn: 'Test-day checklist',
    titleMy: 'စာမေးပွဲနေ့ စစ်ဆေးစာရင်း',
    skillEn: 'Tell practice in this course apart from the official test',
    skillMy: 'ဒီသင်တန်းက လေ့ကျင့်ခန်းနဲ့ တရားဝင်စာမေးပွဲကို ခွဲပြောနိုင်သည်',
    introEn: 'A finished checklist here is not a license and not a DMV result.',
    introMy: 'ဒီစာရင်းပြီးတာက လိုင်စင် မဟုတ်ပါ။ DMV ရလဒ်လည်း မဟုတ်ပါ။',
    bodyEn: `## What this course can do

The practice exam in this path uses the length and pass bar set for this course. Those settings are not the official knowledge test.

This app does not book a DMV appointment and does not guarantee a pass.

### On the official test

The California knowledge test is multiple choice. The questions are drawn from the California Driver Handbook. You get 3 attempts per application. The test is in English. Burmese on these cards is a study aid. Read each official question all the way through.

A drive retest fee is $9.
`,
    bodyMy: `## ဒီသင်တန်း လုပ်ပေးနိုင်တာ

ဒီလမ်းကြောင်းက လေ့ကျင့်စာမေးပွဲက ဒီသင်တန်းအတွက် သတ်မှတ်ထားတဲ့ မေးခွန်းအရေအတွက်နဲ့ အောင်မှတ်ကို သုံးပါတယ်။ အဲဒါက တရားဝင် အသိပညာစာမေးပွဲ မဟုတ်ပါ။

ဒီအက်ပ်က DMV ချိန်းဆို မယူပေးပါ။ အောင်မယ်လို့လည်း အာမမခံပါ။

### တရားဝင်စာမေးပွဲမှာ

အသိပညာစာမေးပွဲက ရွေးချယ်မေးခွန်းပါ။ မေးခွန်းတွေကို ကယ်လီဖိုးနီးယား ယာဉ်မောင်းလက်စွဲက ထုတ်ပါတယ်။ လျှောက်လွှာတစ်ခုမှာ ၃ ကြိမ် ဖြေခွင့်ရှိပါတယ်။ စာမေးပွဲက အင်္ဂလိပ်လိုပါ။ ဒီကတ်ပေါ်က မြန်မာစာက လေ့လာဖို့ အကူအညီပါ။ တရားဝင်မေးခွန်းကို အဆုံးထိ ဖတ်ပါ။

ယာဉ်မောင်းစာမေးပွဲ ပြန်ဖြေရင် ကြေးက ၉ ဒေါ်လာပါ။
`,
    takeawaysEn: [
      'The knowledge test is multiple choice, drawn from the handbook, with 3 attempts per application.',
      'A drive retest fee is $9. Practice scores are not a DMV result.',
    ],
    takeawaysMy: [
      'အသိပညာစာမေးပွဲက ရွေးချယ်မေးခွန်းပါ။ လက်စွဲက ထုတ်ပြီး လျှောက်လွှာတစ်ခုမှာ ၃ ကြိမ် ဖြေခွင့်ရှိပါတယ်။',
      'ယာဉ်မောင်းစာမေးပွဲ ပြန်ဖြေကြေးက ၉ ဒေါ်လာပါ။ လေ့ကျင့်ရမှတ်က DMV ရလဒ် မဟုတ်ပါ။',
    ],
    canEn: ['Name the difference between this practice exam and the official test'],
    canMy: ['ဒီလေ့ကျင့်စာမေးပွဲနဲ့ တရားဝင်စာမေးပွဲ ကွာခြားချက်ကို ပြောနိုင်သည်'],
    taskEn: 'Take one practice exam in this course and write down the score.',
    taskMy: 'ဒီသင်တန်းမှာ လေ့ကျင့်စာမေးပွဲတစ်ခု လုပ်ပြီး ရမှတ်ကို ချရေးပါ။',
    cards: [
      {
        lines_en: ['This course does not book the DMV.', 'The practice exam length and pass bar are set for this course.', 'A high score here is not a license.'],
        lines_my: ['ဒီသင်တန်းက DMV ချိန်းဆို မယူပေးပါ။', 'လေ့ကျင့်စာမေးပွဲ အရေအတွက်နဲ့ အောင်မှတ်ကို ဒီသင်တန်းအတွက် သတ်မှတ်ထားပါတယ်။', 'ဒီမှာ ရမှတ်မြင့်တာက လိုင်စင် မဟုတ်ပါ။'],
        key_en: 'The practice exam is not the official test.',
        key_my: 'လေ့ကျင့်စာမေးပွဲက တရားဝင်စာမေးပွဲ မဟုတ်ပါ။',
        takeaway_en: 'There is no certificate on this card.',
        takeaway_my: 'ဒီကတ်ပေါ်မှာ လက်မှတ် မရှိပါ။',
      },
      {
        lines_en: ['The knowledge test is multiple choice. Questions are drawn from the California Driver Handbook.', 'You get 3 attempts per application. The test is in English. Burmese on these cards is a study aid.'],
        lines_my: ['အသိပညာစာမေးပွဲက ရွေးချယ်မေးခွန်းပါ။ မေးခွန်းတွေကို ကယ်လီဖိုးနီးယား ယာဉ်မောင်းလက်စွဲက ထုတ်ပါတယ်။', 'လျှောက်လွှာတစ်ခုမှာ ၃ ကြိမ် ဖြေခွင့်ရှိပါတယ်။ စာမေးပွဲက အင်္ဂလိပ်လိုပါ။ ဒီကတ်ပေါ်က မြန်မာစာက လေ့လာအကူပါ။'],
        key_en: 'Three attempts per application, from the handbook.',
        key_my: 'လျှောက်လွှာတစ်ခုမှာ ၃ ကြိမ်၊ လက်စွဲက မေးခွန်း။',
        takeaway_en: 'Read the handbook. The test is multiple choice.',
        takeaway_my: 'လက်စွဲကို ဖတ်ပါ။ စာမေးပွဲက ရွေးချယ်မေးခွန်းပါ။',
      },
      {
        lines_en: ['A drive retest fee is $9.', 'Bring the identification the DMV asked you to bring, and arrive for the appointment you made with them.'],
        lines_my: ['ယာဉ်မောင်းစာမေးပွဲ ပြန်ဖြေရင် ကြေးက ၉ ဒေါ်လာပါ။', 'DMV ပြောထားတဲ့ သက်သေခံကို ယူသွားပြီး သူတို့နဲ့ ချိန်းထားတဲ့ အချိန်မီ ရောက်ပါ။'],
        key_en: 'Use the rules you practiced. The office sets the rest.',
        key_my: 'လေ့ကျင့်ထားတဲ့ စည်းကို သုံးပါ။ ကျန်တာကို ရုံးက သတ်မှတ်သည်။',
        takeaway_en: 'This checklist does not replace the DMV instructions.',
        takeaway_my: 'ဒီစာရင်းက DMV ညွှန်ကြားချက်နေရာ မယူပါ။',
      },
    ],
  }),
];

function gatedPath(
  n: number,
  slug: string,
  titleEn: string,
  titleMy: string,
  summaryEn: string,
  summaryMy: string,
  duration: string,
  order: number
): ContentPath {
  return {
    id: seedId(n),
    slug,
    title_en: titleEn,
    title_my: titleMy,
    summary_en: summaryEn,
    summary_my: summaryMy,
    body_en: summaryEn,
    body_my: summaryMy,
    duration,
    level: 'beginner',
    is_premium: true,
    launch_ready: false,
    primary_language: 'my',
    glossary: [],
    status: 'published',
    sort_order: order,
    rating: 4.8,
    enrolled: 0,
    tags: [],
    modules: [],
  };
}

export const LEARNING_CONTENT_SEED: ContentPath[] = [
  {
    id: pathIds.america,
    slug: 'new-to-america',
    title_en: 'New to America: Your Essential Guide',
    title_my: 'အမေရိကကို အသစ်ရောက်သူများအတွက် အခြေခံလမ်းညွှန်',
    summary_en: 'Orientation, a U.S. phone number, and home internet. This free path is ready to finish.',
    summary_my: 'အစပိုင်း၊ အမေရိကန်ဖုန်းနံပါတ်၊ နှင့် အိမ်သုံးအင်တာနက်။ ဒီအခမဲ့လမ်းကြောင်းကို ပြီးအောင် လုပ်နိုင်ပါတယ်။',
    body_en: 'Start with the emergency number, a phone you can use for banks and jobs, and a way to get online without a long contract.',
    body_my: 'အရေးပေါ်နံပါတ်၊ ဘဏ်နဲ့ အလုပ်အတွက် သုံးနိုင်တဲ့ ဖုန်း၊ နှင့် ရှည်တဲ့စာချုပ်မလိုဘဲ အွန်လိုင်းတက်နည်းကနေ စပါ။',
    duration: '4–6 weeks',
    level: 'beginner',
    is_premium: false,
    launch_ready: true,
    primary_language: 'my',
    glossary: [],
    status: 'published',
    sort_order: 1,
    rating: 4.8,
    enrolled: 0,
    tags: ['arrival', 'free'],
    modules: [
      moduleShell(
        moduleIds.welcome,
        pathIds.america,
        'welcome-orientation',
        1,
        'Welcome & Orientation',
        'ကြိုဆိုခြင်းနှင့် အစပိုင်း',
        'Emergency number and a short check.',
        'အရေးပေါ်နံပါတ်နဲ့ တိုတဲ့ စစ်ဆေးချက်။',
        americaLessons.filter((lesson) => lesson.module_id === moduleIds.welcome)
      ),
      moduleShell(
        moduleIds.arrival,
        pathIds.america,
        'arrival-setup',
        2,
        'Arrival & Initial Setup',
        'ရောက်ရှိပြီး ပထမဆုံး ပြင်ဆင်မှု',
        'Phone and internet for your first weeks.',
        'ပထမအပတ်တွေအတွက် ဖုန်းနဲ့ အင်တာနက်။',
        americaLessons.filter((lesson) => lesson.module_id === moduleIds.arrival)
      ),
    ],
  },
  {
    id: pathIds.budget,
    slug: 'budgeting-start',
    title_en: 'Budgeting for a Fresh Start',
    title_my: 'အသစ်စတင်ရန် ဘတ်ဂျက်',
    summary_en: 'See where your money goes, pay needs first, and start a small cushion.',
    summary_my: 'ငွေဘယ်ကို သွားသလဲ ကြည့်ပါ၊ လိုအပ်ချက်ကို အရင်ပေးပါ၊ အရန်ငယ် စပါ။',
    body_en: 'A short free path for a first month in the United States.',
    body_my: 'အမေရိကန်မှာ ပထမလအတွက် တိုတဲ့ အခမဲ့လမ်းကြောင်းပါ။',
    duration: '2–3 weeks',
    level: 'beginner',
    is_premium: false,
    launch_ready: true,
    primary_language: 'my',
    glossary: [],
    status: 'published',
    sort_order: 2,
    rating: 4.8,
    enrolled: 0,
    tags: ['money', 'free'],
    modules: [
      moduleShell(
        moduleIds.budgetSee,
        pathIds.budget,
        'see-where-money-goes',
        1,
        'See where money goes',
        'ငွေဘယ်ကို သွားသလဲ ကြည့်ပါ',
        'A first picture of income and costs, before you try to save.',
        'စုဖို့ မကြိုးစားခင် ဝင်ငွေနဲ့ ကုန်ကျစရိတ်ပုံ။',
        budgetLessons.filter((lesson) => lesson.module_id === moduleIds.budgetSee)
      ),
      moduleShell(
        moduleIds.budgetPlan,
        pathIds.budget,
        'first-month-plan',
        2,
        'Make a first-month plan',
        'ပထမလ အစီအစဉ်လုပ်ပါ',
        'Turn the list into a plan you can follow this month.',
        'စာရင်းကို ဒီလ လိုက်နိုင်တဲ့ အစီအစဉ်ဖြစ်အောင် လုပ်ပါ။',
        budgetLessons.filter((lesson) => lesson.module_id === moduleIds.budgetPlan)
      ),
    ],
  },
  {
    id: pathIds.health,
    slug: 'health-insurance',
    title_en: 'Health Insurance Basics',
    title_my: 'ကျန်းမာရေးအာမခံ အခြေခံ',
    summary_en: 'The words on an insurance card, where coverage comes from, and where to go when you are sick.',
    summary_my: 'အာမခံကတ်ပေါ်က စကားလုံး၊ အာမခံဘယ်ကလာသလဲ၊ ဖျားရင် ဘယ်သွားမလဲ။',
    body_en: 'A short free path. Emergencies still come before paperwork.',
    body_my: 'တိုတဲ့ အခမဲ့လမ်းကြောင်းပါ။ အရေးပေါ်က စာရွက်စာတမ်းထက် အရင်ပါ။',
    duration: '3–4 weeks',
    level: 'beginner',
    is_premium: false,
    launch_ready: true,
    primary_language: 'my',
    glossary: [],
    status: 'published',
    sort_order: 3,
    rating: 4.8,
    enrolled: 0,
    tags: ['health', 'free'],
    modules: [
      moduleShell(
        moduleIds.healthWords,
        pathIds.health,
        'how-coverage-works',
        1,
        'How coverage works',
        'အာမခံ ဘယ်လို အလုပ်လုပ်သလဲ',
        'The words and the places coverage comes from.',
        'စကားလုံးတွေနဲ့ အာမခံဘယ်ကလာသလဲ။',
        healthLessons.filter((lesson) => lesson.module_id === moduleIds.healthWords)
      ),
      moduleShell(
        moduleIds.healthUse,
        pathIds.health,
        'use-care',
        2,
        'Use care without a surprise bill',
        'မထင်မှတ်တဲ့ ဘီလ်မကုန်ဘဲ ကုသမှုယူပါ',
        'Choose the right place and bring what the desk will ask for.',
        'နေရာမှန်ရွေးပြီး ကောင်တာမေးမည့်အရာကို ယူသွားပါ။',
        healthLessons.filter((lesson) => lesson.module_id === moduleIds.healthUse)
      ),
    ],
  },
  gatedPath(4, 'renting-id-setup', 'Renting, Banking & ID Setup', 'ငှားရမ်းခြင်း၊ ဘဏ်၊ နှင့် ID', 'Published with no lessons yet.', 'သင်ခန်းစာ မရှိသေးပါ။', '4–5 weeks', 4),
  gatedPath(5, 'build-your-credit-foundation', 'Build Your Credit Foundation', 'ခရက်ဒစ် အခြေခံတည်ဆောက်ခြင်း', 'Published with no lessons yet.', 'သင်ခန်းစာ မရှိသေးပါ။', '3–4 weeks', 5),
  gatedPath(6, 'preparing-for-your-golden-years', 'Preparing for Your Golden Years', 'အငြိမ်းစားအတွက် ပြင်ဆင်ခြင်း', 'Not ready to start.', 'မစသေးပါ။', '3 hours', 6),
  gatedPath(7, 'self-employment', 'Self-Employment & Side Income', 'ကိုယ်ပိုင်အလုပ်နှင့် ဘေးဝင်ငွေ', 'Published with no lessons yet.', 'သင်ခန်းစာ မရှိသေးပါ။', '6–8 weeks', 7),
  gatedPath(8, 'us-taxes', 'Understanding U.S. Taxes', 'အမေရိကန်အခွန်ကို နားလည်ခြင်း', 'Published with no lessons yet.', 'သင်ခန်းစာ မရှိသေးပါ။', '4–5 weeks', 8),
  {
    id: dmvPathId,
    slug: 'ca-dmv',
    title_en: 'California Driving Test',
    title_my: 'ကယ်လီဖိုးနီးယား ယာဉ်မောင်းစာမေးပွဲ',
    summary_en: 'Practice for the California knowledge test. It is multiple choice, drawn from the handbook, and in English. Scores here are not a DMV result.',
    summary_my: 'ကယ်လီဖိုးနီးယား အသိပညာစာမေးပွဲအတွက် လေ့ကျင့်ခန်း။ ရွေးချယ်မေးခွန်း၊ လက်စွဲက ထုတ်ပြီး အင်္ဂလိပ်လို ဖြေရပါတယ်။ ဒီရမှတ်က DMV ရလဒ် မဟုတ်ပါ။',
    body_en: 'English is the main text because the DMV test is in English. Burmese sits under each block.',
    body_my: 'DMV စာမေးပွဲက အင်္ဂလိပ်လိုမို့ အင်္ဂလိပ်စာက အဓိကပါ။ မြန်မာစာက အပိုဒ်အောက်မှာ ပါပါတယ်။',
    duration: '1 week',
    level: 'beginner',
    is_premium: false,
    launch_ready: true,
    primary_language: 'en',
    exam_question_count: 36,
    exam_pass_percent: 83,
    glossary: [
      { term_en: 'right-of-way', term_my: 'လမ်းဦးစားပေး' },
      { term_en: 'yield', term_my: 'လမ်းလွှဲပေးပါ' },
      { term_en: 'pedestrian', term_my: 'လမ်းလျှောက်သူ' },
      { term_en: 'crosswalk', term_my: 'လူကူးမျဉ်းကျား' },
      { term_en: 'intersection', term_my: 'လမ်းဆုံ' },
    ],
    status: 'published',
    sort_order: 9,
    rating: 4.8,
    enrolled: 0,
    tags: ['driving', 'california'],
    modules: [
      moduleShell(dmvModuleId, dmvPathId, 'dmv-rules', 1, 'Starter rules', 'အစ စည်းမျဉ်း', 'Right-of-way, then a short word check.', 'လမ်းဦးစားပေး၊ ပြီးရင် စကားလုံးစစ်ဆေးချက်။', dmvLessons, 'starter'),
      moduleShell(dmvSignsId, dmvPathId, 'dmv-signs', 2, 'Signs', 'ဆိုင်းဘုတ်', 'Shapes you can recognize before you read the words.', 'စာမဖတ်ခင် မှတ်မိနိုင်တဲ့ ပုံများ။', dmvSignLessons, 'intermediate'),
      moduleShell(dmvShareId, dmvPathId, 'dmv-sharing', 3, 'Sharing the road', 'လမ်းမျှဝေခြင်း', 'Left turns, school buses, roundabouts, and headlights.', 'ဘယ်ကွေ့၊ ကျောင်းကား၊ အဝိုင်းလမ်း၊ ရှေ့မီး။', dmvShareLessons, 'advanced'),
      moduleShell(dmvGradId, dmvPathId, 'dmv-graduation', 4, 'Test day', 'စာမေးပွဲနေ့', 'A checklist. Not a certificate.', 'စစ်ဆေးစာရင်းပါ။ လက်မှတ် မဟုတ်ပါ။', dmvGradLessons, 'graduation'),
    ],
  },
];
